import type { ConfiguratorEstimate, ConfiguratorState, HolePoint } from './configurator-math';

import {
  formatNumber,
  formatWeight,
  getHoleHalfExtents,
  getHoleLayout,
  getProductBendLines,
  getProductInternalCuts,
  getSelectedOptions,
} from './configurator-math';
import { buildOutlineGeometry } from './configurator-outline';
import { resolvePartGeometry, type GeometryPath, type PartFeature } from './part-geometry';

type PdfJpegImage = { data: Uint8Array; width: number; height: number };

const QUOTE_ID = '#LX-2026-0142';
const A3_LANDSCAPE = { width: 1190.55, height: 841.89 };
const DIAMETER_SYMBOL = String.fromCharCode(216);
const PLUS_MINUS_SYMBOL = String.fromCharCode(177);
const MULTIPLY_SYMBOL = String.fromCharCode(215);
const DEGREE_SYMBOL = String.fromCharCode(176);
const PDF_CUSTOM_GLYPHS: Record<string, number> = {
  '\u010c': 128,
  '\u010d': 129,
  '\u010e': 130,
  '\u010f': 131,
  '\u013d': 132,
  '\u013e': 133,
  '\u0147': 134,
  '\u0148': 135,
  '\u0160': 136,
  '\u0161': 137,
  '\u0164': 138,
  '\u0165': 139,
  '\u017d': 140,
  '\u017e': 141,
  '\u0139': 142,
  '\u013a': 143,
  '\u0154': 144,
  '\u0155': 145,
};
const PDF_UNSUPPORTED_FALLBACKS: Record<string, string> = {
  '\u0106': 'C',
  '\u0107': 'c',
  '\u0110': 'D',
  '\u0111': 'd',
};
const PDF_FONT_ENCODING =
  '<< /Type /Encoding /BaseEncoding /WinAnsiEncoding /Differences [128 /Ccaron /ccaron /Dcaron /dcaron /Lcaron /lcaron /Ncaron /ncaron /Scaron /scaron /Tcaron /tcaron /Zcaron /zcaron /Lacute /lacute /Racute /racute] >>';

type Point = { x: number; y: number };
type Rgb = [number, number, number];
type ThreeModule = typeof import('three');

function todayLabel() {
  return new Intl.DateTimeFormat('sk-SK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date());
}

function normalizePdfText(value: string | number) {
  return String(value)
    .replace(/\u20ac/g, ' EUR')
    .replace(/\s+/g, ' ')
    .trim();
}

function encodePdfText(value: string | number) {
  return Array.from(normalizePdfText(value))
    .map(char => {
      const customCode = PDF_CUSTOM_GLYPHS[char];
      if (customCode) return String.fromCharCode(customCode);

      const code = char.charCodeAt(0);
      if (code <= 255) return char;

      return PDF_UNSUPPORTED_FALLBACKS[char] ?? '';
    })
    .join('');
}

function escapePdfText(value: string | number) {
  return encodePdfText(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function hexToRgb(hex: string): Rgb {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : normalized,
    16,
  );

  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

function shadeHex(hex: string, amount: number): Rgb {
  const [r, g, b] = hexToRgb(hex).map(channel => channel * 255) as Rgb;
  const target = amount >= 0 ? 255 : 0;
  const ratio = Math.abs(amount);
  return [
    (r + (target - r) * ratio) / 255,
    (g + (target - g) * ratio) / 255,
    (b + (target - b) * ratio) / 255,
  ];
}

function holeCallout(holes: HolePoint[]) {
  const groups = new Map<string, { count: number; hole: HolePoint }>();
  holes.forEach(hole => {
    const key =
      hole.shape === 'slot'
        ? `slot-${Math.round(hole.slotWidthMm * 10)}-${Math.round(hole.slotLengthMm * 10)}`
        : hole.shape === 'rect'
          ? `rect-${Math.round(hole.slotWidthMm * 10)}-${Math.round(hole.slotLengthMm * 10)}`
          : `round-${Math.round(hole.diameterMm * 10)}`;
    const group = groups.get(key);
    if (group) group.count += 1;
    else groups.set(key, { count: 1, hole });
  });

  return [...groups.values()]
    .map(({ count, hole }) =>
      hole.shape === 'slot'
        ? `${count} ${MULTIPLY_SYMBOL} OVÁL ${compactNumber(hole.slotWidthMm)} ${MULTIPLY_SYMBOL} ${compactNumber(hole.slotLengthMm)} SKRZ`
        : hole.shape === 'rect'
          ? `${count} ${MULTIPLY_SYMBOL} OBDĹŽNIK ${compactNumber(hole.slotWidthMm)} ${MULTIPLY_SYMBOL} ${compactNumber(hole.slotLengthMm)} SKRZ`
          : `${count} ${MULTIPLY_SYMBOL} ${DIAMETER_SYMBOL}${compactNumber(hole.diameterMm)} SKRZ`,
    )
    .join(' / ');
}

function compactNumber(value: number, maxFractionDigits = 3) {
  const factor = 10 ** maxFractionDigits;
  const rounded = Math.round(value * factor) / factor;
  return new Intl.NumberFormat('sk-SK', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  }).format(rounded);
}

function mm(value: number, maxFractionDigits = 3) {
  return `${compactNumber(value, maxFractionDigits)} mm`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

type CornerTreatmentRow = {
  label: string;
  corner: 'TopLeft' | 'TopRight' | 'BottomRight' | 'BottomLeft';
  type: string;
  value: string;
  sizeMm: number;
  chamferXMm: number;
  chamferYMm: number;
};

function getCornerTreatmentRows(config: ConfiguratorState): CornerTreatmentRow[] {
  const corners = [
    ['LH', 'TopLeft'],
    ['PH', 'TopRight'],
    ['PD', 'BottomRight'],
    ['LD', 'BottomLeft'],
  ] as const;

  return corners.map(([label, corner]) => {
    const type = String(config[`corner${corner}Type` as keyof ConfiguratorState] ?? 'square');
    const size = Number(config[`corner${corner}Mm` as keyof ConfiguratorState] ?? 0);
    const chamferX = Number(config[`corner${corner}ChamferXMm` as keyof ConfiguratorState] ?? size);
    const chamferY = Number(config[`corner${corner}ChamferYMm` as keyof ConfiguratorState] ?? size);

    if (type === 'square') {
      return {
        label,
        corner,
        type,
        value: 'rov.',
        sizeMm: size,
        chamferXMm: chamferX,
        chamferYMm: chamferY,
      };
    }
    if (type === 'radius') {
      return {
        label,
        corner,
        type,
        value: `R${compactNumber(size)}`,
        sizeMm: size,
        chamferXMm: chamferX,
        chamferYMm: chamferY,
      };
    }
    if (type === 'chamfer-irregular') {
      return {
        label,
        corner,
        type,
        value: `sekn. ${compactNumber(chamferX)}${MULTIPLY_SYMBOL}${compactNumber(chamferY)}`,
        sizeMm: size,
        chamferXMm: chamferX,
        chamferYMm: chamferY,
      };
    }

    return {
      label,
      corner,
      type,
      value: `sekn. ${compactNumber(size)}`,
      sizeMm: size,
      chamferXMm: chamferX,
      chamferYMm: chamferY,
    };
  });
}

function cornerTreatmentLabel(config: ConfiguratorState) {
  return getCornerTreatmentRows(config)
    .map(row => `${row.label}: ${row.value}`)
    .join(' / ');
}

function cornerTreatmentShortLabel(config: ConfiguratorState) {
  const rows = getCornerTreatmentRows(config);
  const treatedRows = rows.filter(row => row.type !== 'square');
  if (treatedRows.length === 0)
    return planCornerRadiusMm(config) > 0
      ? `4${MULTIPLY_SYMBOL} R${compactNumber(planCornerRadiusMm(config))}`
      : 'rovné';

  const values = new Set(treatedRows.map(row => row.value));
  if (treatedRows.length === 4 && values.size === 1)
    return `4${MULTIPLY_SYMBOL} ${treatedRows[0].value}`;

  return 'viď hlavný pohľad';
}
function planCornerRadiusMm(config: ConfiguratorState) {
  return Math.max(0, Math.min(config.edgeRadiusMm, Math.min(config.widthMm, config.lengthMm) / 2));
}

function uniqueSorted(values: number[]) {
  return [...new Set(values.map(value => Math.round(value * 1000) / 1000))].sort((a, b) => a - b);
}

function staggerCloseLabels(
  positions: Array<{ key: number; position: number }>,
  minDistance = 12,
  step = 7,
) {
  const offsets = new Map<number, number>();
  let group: Array<{ key: number; position: number }> = [];
  const flushGroup = () => {
    if (group.length === 0) return;
    const center = (group.length - 1) / 2;
    group.forEach((item, index) => {
      const directionIndex = index - center;
      offsets.set(item.key, directionIndex * step);
    });
    group = [];
  };

  positions
    .slice()
    .sort((a, b) => a.position - b.position)
    .forEach(item => {
      const previous = group[group.length - 1];
      if (!previous || item.position - previous.position <= minDistance) {
        group.push(item);
        return;
      }
      flushGroup();
      group.push(item);
    });
  flushGroup();

  return offsets;
}

function slotPolygonPoints(cx: number, cy: number, width: number, height: number, angleDeg = 0) {
  const safeWidth = Math.max(width, 0.1);
  const safeHeight = Math.max(height, 0.1);
  const r = Math.min(safeWidth, safeHeight) / 2;
  const steps = 14;
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const rotate = (x: number, y: number) => ({
    x: cx + x * cos - y * sin,
    y: cy + x * sin + y * cos,
  });
  const localPoints: Point[] = [];

  if (safeHeight >= safeWidth) {
    const straight = (safeHeight - safeWidth) / 2;
    const topCenterY = -straight;
    const bottomCenterY = straight;
    for (let i = 0; i <= steps; i += 1) {
      const theta = Math.PI + (Math.PI * i) / steps;
      localPoints.push({ x: Math.cos(theta) * r, y: topCenterY + Math.sin(theta) * r });
    }
    for (let i = 0; i <= steps; i += 1) {
      const theta = (Math.PI * i) / steps;
      localPoints.push({ x: Math.cos(theta) * r, y: bottomCenterY + Math.sin(theta) * r });
    }
    return localPoints.map(point => rotate(point.x, point.y));
  }

  const straight = (safeWidth - safeHeight) / 2;
  const leftCenterX = -straight;
  const rightCenterX = straight;
  for (let i = 0; i <= steps; i += 1) {
    const theta = -Math.PI / 2 + (Math.PI * i) / steps;
    localPoints.push({ x: rightCenterX + Math.cos(theta) * r, y: Math.sin(theta) * r });
  }
  for (let i = 0; i <= steps; i += 1) {
    const theta = Math.PI / 2 + (Math.PI * i) / steps;
    localPoints.push({ x: leftCenterX + Math.cos(theta) * r, y: Math.sin(theta) * r });
  }

  return localPoints.map(point => rotate(point.x, point.y));
}

function rectPolygonPoints(
  cx: number,
  cy: number,
  width: number,
  height: number,
  angleDeg = 0,
  cornerRadius = 0,
) {
  const safeWidth = Math.max(width, 0.1);
  const safeHeight = Math.max(height, 0.1);
  const radius = Math.min(Math.max(cornerRadius, 0), safeWidth / 2, safeHeight / 2);
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const rotate = (x: number, y: number) => ({
    x: cx + x * cos - y * sin,
    y: cy + x * sin + y * cos,
  });

  if (radius <= 0.01) {
    return [
      rotate(-safeWidth / 2, -safeHeight / 2),
      rotate(safeWidth / 2, -safeHeight / 2),
      rotate(safeWidth / 2, safeHeight / 2),
      rotate(-safeWidth / 2, safeHeight / 2),
    ];
  }

  const points: Point[] = [];
  const steps = 8;
  const addCorner = (cornerX: number, cornerY: number, start: number, end: number) => {
    for (let index = 0; index <= steps; index += 1) {
      const theta = start + ((end - start) * index) / steps;
      points.push(rotate(cornerX + Math.cos(theta) * radius, cornerY + Math.sin(theta) * radius));
    }
  };

  addCorner(safeWidth / 2 - radius, -safeHeight / 2 + radius, -Math.PI / 2, 0);
  addCorner(safeWidth / 2 - radius, safeHeight / 2 - radius, 0, Math.PI / 2);
  addCorner(-safeWidth / 2 + radius, safeHeight / 2 - radius, Math.PI / 2, Math.PI);
  addCorner(-safeWidth / 2 + radius, -safeHeight / 2 + radius, Math.PI, Math.PI * 1.5);

  return points;
}

function rotatedPoint(cx: number, cy: number, dx: number, dy: number, angleDeg = 0): Point {
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

function drawCenterMark(
  page: PdfPage,
  cx: number,
  cy: number,
  width: number,
  height: number,
  angleDeg: number,
  color: Rgb,
) {
  const major = Math.max(12, width + 12);
  const minor = Math.max(12, height + 12);
  const majorA = rotatedPoint(cx, cy, -major / 2, 0, angleDeg);
  const majorB = rotatedPoint(cx, cy, major / 2, 0, angleDeg);
  const minorA = rotatedPoint(cx, cy, 0, -minor / 2, angleDeg);
  const minorB = rotatedPoint(cx, cy, 0, minor / 2, angleDeg);

  page.strokeColor(color);
  page.lineWidth(0.24);
  page.dash([8, 3, 2, 3]);
  page.line(majorA.x, majorA.y, majorB.x, majorB.y);
  page.line(minorA.x, minorA.y, minorB.x, minorB.y);
  page.dash();
}

function drawRoundedSlotReferenceLines(
  page: PdfPage,
  cx: number,
  cy: number,
  length: number,
  width: number,
  radius: number,
  angleDeg: number,
  color: Rgb,
  straightDimensionLabel: string,
) {
  const safeRadius = Math.max(0, Math.min(radius, length / 2, width / 2));
  const straightHalf = Math.max(0, length / 2 - safeRadius);

  if (straightHalf <= 0.5) {
    drawCenterMark(page, cx, cy, length, width, angleDeg, color);
    return;
  }

  const lineHalf = width / 2 + 5;
  const dimensionOffset = width / 2 + 8;
  const dimensionStart = rotatedPoint(cx, cy, -straightHalf, dimensionOffset, angleDeg);
  const dimensionEnd = rotatedPoint(cx, cy, straightHalf, dimensionOffset, angleDeg);
  const labelCenter = rotatedPoint(cx, cy, 0, dimensionOffset + 8, angleDeg);
  const labelWidth = Math.max(30, straightDimensionLabel.length * 3.2 + 8);
  const labelHeight = 10;

  page.strokeColor(color);
  page.fillColor(color);
  page.lineWidth(0.24);
  page.dash([7, 3, 2, 3]);
  [-straightHalf, straightHalf].forEach(offset => {
    const start = rotatedPoint(cx, cy, offset, -lineHalf, angleDeg);
    const end = rotatedPoint(cx, cy, offset, lineHalf, angleDeg);
    page.line(start.x, start.y, end.x, end.y);
  });
  page.dash();
  page.lineWidth(0.32);
  page.line(dimensionStart.x, dimensionStart.y, dimensionEnd.x, dimensionEnd.y);
  drawDimensionArrow(page, dimensionStart, angleDeg + 180, color);
  drawDimensionArrow(page, dimensionEnd, angleDeg, color);

  page.fillColor([1, 1, 1]);
  page.strokeColor(color);
  page.lineWidth(0.28);
  page.rect(
    labelCenter.x - labelWidth / 2,
    labelCenter.y - labelHeight / 2,
    labelWidth,
    labelHeight,
    'B',
  );
  page.text(labelCenter.x, labelCenter.y + 2.8, straightDimensionLabel, {
    size: 5.6,
    align: 'center',
    color,
  });
}

function drawDimensionArrow(page: PdfPage, tip: Point, angleDeg: number, color: Rgb) {
  const size = 3.2;
  const spread = Math.PI / 7;
  const angle = (angleDeg * Math.PI) / 180;

  page.fillColor(color);
  page.polygon(
    [
      tip,
      {
        x: tip.x - Math.cos(angle - spread) * size,
        y: tip.y - Math.sin(angle - spread) * size,
      },
      {
        x: tip.x - Math.cos(angle + spread) * size,
        y: tip.y - Math.sin(angle + spread) * size,
      },
    ],
    'f',
  );
}

function straightPartMm(widthMm: number, lengthMm: number, radiusMm: number) {
  return Math.max(0, Math.max(widthMm, lengthMm) - radiusMm * 2);
}

function straightPartNote(widthMm: number, lengthMm: number, radiusMm: number) {
  if (radiusMm <= 0.001) return '';

  return `rovn\u00e1 \u010das\u0165 ${compactNumber(straightPartMm(widthMm, lengthMm, radiusMm))} mm`;
}

function isPillRadius(widthMm: number, lengthMm: number, radiusMm = 0) {
  return radiusMm > 0 && radiusMm >= Math.min(widthMm, lengthMm) / 2 - 0.01;
}

function drawFeatureDimensionTag(
  page: PdfPage,
  target: Point,
  box: { x: number; y: number; width: number; height: number },
  featureWidth: number,
  lines: string[],
  color: Rgb,
) {
  const normalizedLines = lines.filter(Boolean);
  if (normalizedLines.length === 0) return;

  const textWidth = Math.max(...normalizedLines.map(line => line.length)) * 3.65;
  const isCompactId = normalizedLines.length === 1 && /^[ORV]\d+$/.test(normalizedLines[0] ?? '');
  const tagWidth = Math.max(isCompactId ? 23 : 48, textWidth + (isCompactId ? 9 : 12));
  const tagHeight = 8 + normalizedLines.length * 8.4;
  const rightX = target.x + featureWidth / 2 + 15;
  const leftX = target.x - featureWidth / 2 - tagWidth - 15;
  const placeRight = rightX + tagWidth <= box.x + box.width - 6 || leftX < box.x + 6;
  const tagX = clamp(placeRight ? rightX : leftX, box.x + 6, box.x + box.width - tagWidth - 6);
  const tagY = clamp(target.y - tagHeight / 2, box.y + 6, box.y + box.height - tagHeight - 6);
  const leaderX = placeRight ? tagX : tagX + tagWidth;

  page.strokeColor(color);
  page.lineWidth(0.32);
  page.line(target.x, target.y, leaderX, tagY + tagHeight / 2);
  page.fillColor([1, 1, 1]);
  page.rect(tagX, tagY, tagWidth, tagHeight, 'B');
  normalizedLines.forEach((line, index) => {
    page.text(isCompactId ? tagX + tagWidth / 2 : tagX + 6, tagY + 11 + index * 8.4, line, {
      size: isCompactId ? 5.8 : 6.1,
      align: isCompactId ? 'center' : 'left',
      color,
    });
  });
}

class PdfPage {
  private ops: string[] = [];

  constructor(
    readonly width: number,
    readonly height: number,
  ) {}

  private y(value: number) {
    return this.height - value;
  }

  private n(value: number) {
    return Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : '0';
  }

  private color(value: Rgb) {
    return value.map(channel => this.n(channel)).join(' ');
  }

  raw(command: string) {
    this.ops.push(command);
  }

  strokeColor(color: Rgb) {
    this.raw(`${this.color(color)} RG`);
  }

  fillColor(color: Rgb) {
    this.raw(`${this.color(color)} rg`);
  }

  lineWidth(width: number) {
    this.raw(`${this.n(width)} w`);
  }

  dash(pattern: number[] = []) {
    this.raw(`[${pattern.map(value => this.n(value)).join(' ')}] 0 d`);
  }

  line(x1: number, y1: number, x2: number, y2: number) {
    this.raw(`${this.n(x1)} ${this.n(this.y(y1))} m ${this.n(x2)} ${this.n(this.y(y2))} l S`);
  }

  rect(x: number, y: number, width: number, height: number, mode: 'S' | 'f' | 'B' = 'S') {
    this.raw(
      `${this.n(x)} ${this.n(this.y(y + height))} ${this.n(width)} ${this.n(height)} re ${mode}`,
    );
  }

  polygon(points: Point[], mode: 'S' | 'f' | 'B' = 'S') {
    if (!points.length) return;
    const [first, ...rest] = points;
    this.raw(
      `${this.n(first.x)} ${this.n(this.y(first.y))} m ${rest
        .map(point => `${this.n(point.x)} ${this.n(this.y(point.y))} l`)
        .join(' ')} h ${mode}`,
    );
  }

  circle(cx: number, cy: number, radius: number, mode: 'S' | 'f' | 'B' = 'S') {
    this.ellipse(cx, cy, radius, radius, mode);
  }

  ellipse(cx: number, cy: number, rx: number, ry: number, mode: 'S' | 'f' | 'B' = 'S') {
    const k = 0.5522847498;
    const y = this.y(cy);
    this.raw(
      [
        `${this.n(cx + rx)} ${this.n(y)} m`,
        `${this.n(cx + rx)} ${this.n(y + k * ry)} ${this.n(cx + k * rx)} ${this.n(y + ry)} ${this.n(cx)} ${this.n(y + ry)} c`,
        `${this.n(cx - k * rx)} ${this.n(y + ry)} ${this.n(cx - rx)} ${this.n(y + k * ry)} ${this.n(cx - rx)} ${this.n(y)} c`,
        `${this.n(cx - rx)} ${this.n(y - k * ry)} ${this.n(cx - k * rx)} ${this.n(y - ry)} ${this.n(cx)} ${this.n(y - ry)} c`,
        `${this.n(cx + k * rx)} ${this.n(y - ry)} ${this.n(cx + rx)} ${this.n(y - k * ry)} ${this.n(cx + rx)} ${this.n(y)} c`,
        `h ${mode}`,
      ].join(' '),
    );
  }

  text(
    x: number,
    y: number,
    value: string | number,
    options: { size?: number; align?: 'left' | 'center' | 'right'; color?: Rgb } = {},
  ) {
    const size = options.size ?? 10;
    const text = escapePdfText(value);
    const estimatedWidth = text.length * size * 0.52;
    const tx =
      options.align === 'center'
        ? x - estimatedWidth / 2
        : options.align === 'right'
          ? x - estimatedWidth
          : x;
    this.fillColor(options.color ?? [0, 0, 0]);
    this.raw(`BT /F1 ${this.n(size)} Tf ${this.n(tx)} ${this.n(this.y(y))} Td (${text}) Tj ET`);
  }

  textRotated(
    x: number,
    y: number,
    value: string | number,
    angleDeg: number,
    options: { size?: number; align?: 'left' | 'center' | 'right'; color?: Rgb } = {},
  ) {
    const size = options.size ?? 10;
    const encodedText = escapePdfText(value);
    const estimatedWidth = encodedText.length * size * 0.52;
    const offset =
      options.align === 'center'
        ? -estimatedWidth / 2
        : options.align === 'right'
          ? -estimatedWidth
          : 0;
    const angle = (angleDeg * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    this.fillColor(options.color ?? [0, 0, 0]);
    this.raw(
      `BT /F1 ${this.n(size)} Tf ${this.n(cos)} ${this.n(sin)} ${this.n(-sin)} ${this.n(cos)} ${this.n(x)} ${this.n(this.y(y))} Tm ${this.n(offset)} 0 Td (${encodedText}) Tj ET`,
    );
  }

  textLines(x: number, y: number, lines: Array<string | number>, size = 10, leading = size * 1.35) {
    lines.forEach((line, index) => this.text(x, y + index * leading, line, { size }));
  }

  image(name: string, x: number, y: number, width: number, height: number) {
    this.raw(
      `q ${this.n(width)} 0 0 ${this.n(height)} ${this.n(x)} ${this.n(this.y(y + height))} cm /${name} Do Q`,
    );
  }

  content() {
    return this.ops.join('\n');
  }
}

class PdfDocument {
  private pages: PdfPage[] = [];
  private images: Array<PdfJpegImage & { name: string }> = [];

  addPage(width: number, height: number) {
    const page = new PdfPage(width, height);
    this.pages.push(page);
    return page;
  }

  addJpegImage(image: PdfJpegImage) {
    const name = `Im${this.images.length + 1}`;
    this.images.push({ ...image, name });
    return name;
  }

  bytes() {
    const binary = (bytes: Uint8Array) =>
      Array.from(bytes, byte => String.fromCharCode(byte)).join('');
    const objects: string[] = [
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding ' + PDF_FONT_ENCODING + ' >>',
    ];
    this.images.forEach(image => {
      objects.push(
        `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.data.length} >>\nstream\n${binary(image.data)}\nendstream`,
      );
    });

    const pageCount = this.pages.length;
    const imageIds = this.images.map((_, index) => 2 + index);
    const contentIds = this.pages.map((_, index) => 2 + this.images.length + index);
    const pageIds = this.pages.map((_, index) => 2 + this.images.length + pageCount + index);
    const pagesId = 2 + this.images.length + pageCount * 2;
    const catalogId = pagesId + 1;

    this.pages.forEach(page => {
      const content = page.content();
      objects.push(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    });

    const imageResources = this.images.length
      ? ` /XObject << ${this.images
          .map((image, index) => `/${image.name} ${imageIds[index]} 0 R`)
          .join(' ')} >>`
      : '';

    this.pages.forEach((page, index) => {
      objects.push(
        `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${page.width.toFixed(2)} ${page.height.toFixed(2)}] /Resources << /Font << /F1 1 0 R >>${imageResources} >> /Contents ${contentIds[index]} 0 R >>`,
      );
    });

    objects.push(
      `<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageCount} >>`,
    );
    objects.push(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

    let pdf = '%PDF-1.4\n%\xE2\xE3\xCF\xD3\n';
    const offsets = [0];
    objects.forEach((object, index) => {
      offsets[index + 1] = pdf.length;
      pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
    });
    const xref = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    for (let index = 1; index <= objects.length; index += 1) {
      pdf += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
    }
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xref}\n%%EOF`;

    const bytes = new Uint8Array(pdf.length);
    for (let index = 0; index < pdf.length; index += 1) bytes[index] = pdf.charCodeAt(index) & 255;
    return bytes;
  }
}

function drawFrame(page: PdfPage, label: string) {
  const outer = { x: 16, y: 16, width: page.width - 32, height: page.height - 32 };
  const inner = { x: 34, y: 34, width: page.width - 68, height: page.height - 68 };
  const columns = 8;
  const rows = ['A', 'B', 'C', 'D', 'E', 'F'];
  const columnWidth = inner.width / columns;
  const rowHeight = inner.height / rows.length;

  page.strokeColor([0.2, 0.2, 0.2]);
  page.lineWidth(0.75);
  page.rect(outer.x, outer.y, outer.width, outer.height);
  page.lineWidth(0.95);
  page.rect(inner.x, inner.y, inner.width, inner.height);
  page.lineWidth(0.32);

  for (let index = 1; index < columns; index += 1) {
    const x = inner.x + columnWidth * index;
    page.line(x, outer.y, x, inner.y);
    page.line(x, inner.y + inner.height, x, outer.y + outer.height);
  }

  rows.slice(1).forEach((_, index) => {
    const y = inner.y + rowHeight * (index + 1);
    page.line(outer.x, y, inner.x, y);
    page.line(inner.x + inner.width, y, outer.x + outer.width, y);
  });

  for (let index = 0; index < columns; index += 1) {
    const x = inner.x + columnWidth * (index + 0.5);
    page.text(x, 28, String(index + 1), { size: 6.2, align: 'center', color: [0.25, 0.25, 0.25] });
    page.text(x, page.height - 20, String(index + 1), {
      size: 6.2,
      align: 'center',
      color: [0.25, 0.25, 0.25],
    });
  }

  rows.forEach((row, index) => {
    const y = inner.y + rowHeight * (index + 0.5) + 2;
    page.text(24, y, row, { size: 6.2, align: 'center', color: [0.25, 0.25, 0.25] });
    page.text(page.width - 24, y, row, {
      size: 6.2,
      align: 'center',
      color: [0.25, 0.25, 0.25],
    });
  });

  page.text(inner.x + 8, inner.y + inner.height - 8, label, {
    size: 6.4,
    color: [0.36, 0.36, 0.36],
  });
}

function drawTitleBlock(page: PdfPage, config: ConfiguratorState, estimate: ConfiguratorEstimate) {
  const selected = getSelectedOptions(config);
  const partName = config.configurationName?.trim() || selected.product.name;
  const partNameMaxWidth = 272;
  const maxHelveticaGlyphWidthEm = 1.05;
  const encodedPartNameLength = Math.max(1, encodePdfText(partName).length);
  const partNameFontSize = Math.max(
    8,
    Math.min(15, partNameMaxWidth / (encodedPartNameLength * maxHelveticaGlyphWidthEm)),
  );
  const maxPartNameLength = Math.max(
    4,
    Math.floor(partNameMaxWidth / (partNameFontSize * maxHelveticaGlyphWidthEm)),
  );
  const fittedPartName =
    partName.length > maxPartNameLength
      ? `${partName.slice(0, maxPartNameLength - 3).trimEnd()}...`
      : partName;
  const x = 644;
  const y = 640;
  const w = 512;
  const h = 168;
  page.strokeColor([0.22, 0.22, 0.22]);
  page.fillColor([1, 1, 1]);
  page.lineWidth(0.62);
  page.rect(x, y, w, h, 'B');

  const rowHeights = [24, 24, 24, 24, 72];
  let rowY = y;
  rowHeights.slice(0, -1).forEach(height => {
    rowY += height;
    page.line(x, rowY, x + w, rowY);
  });
  page.line(x + 142, y, x + 142, y + 96);
  page.line(x + 284, y, x + 284, y + 96);
  page.line(x + 382, y, x + 382, y + 96);
  page.line(x + 284, y + 96, x + 284, y + h);
  page.line(x + 382, y + 96, x + 382, y + h);

  const cell = (
    cx: number,
    cy: number,
    label: string,
    value: string,
    width: number,
    size = 8.6,
  ) => {
    page.text(cx + 5, cy + 8, label, { size: 5.9, color: [0.42, 0.42, 0.42] });
    page.text(cx + 5, cy + 19, value, { size });
  };

  cell(x, y, 'Materi\u00e1l', selected.material.name, 142);
  cell(x + 142, y, 'Polotovar', 'V\u00fdpalok', 142);
  cell(x + 284, y, 'Povrch', selected.finish.name, 98);
  cell(x + 382, y, 'Hmotnos\u0165', formatWeight(estimate.weightKg), 130);
  cell(x, y + 24, 'Hr\u00fabka', formatNumber(config.thicknessMm, 1) + ' mm', 142);
  cell(x + 142, y + 24, 'Mierka', 'Pod\u013ea k\u00f3t', 142);
  cell(x + 284, y + 24, 'Form\u00e1t', 'A3', 98);
  cell(x + 382, y + 24, 'Norma', 'ISO 2768-mK', 130);
  cell(x, y + 48, 'V\u00fdkres', 'LX-' + new Date().getFullYear() + '-CFG', 142);
  cell(x + 142, y + 48, 'D\u00e1tum', todayLabel(), 142);
  cell(x + 284, y + 48, 'Rev\u00edzia', '00', 98);
  cell(x + 382, y + 48, 'List', '1 / 1', 130);
  cell(
    x,
    y + 72,
    'Technol\u00f3gia',
    selected.machine.process === 'plasma' ? 'Plazma' : 'Laser',
    142,
  );
  cell(x + 142, y + 72, 'Poloha prvkov', PLUS_MINUS_SYMBOL + '0,2 mm', 142);
  cell(x + 284, y + 72, 'Rohy', cornerTreatmentShortLabel(config), 98, 7.2);
  cell(x + 382, y + 72, 'Objedn\u00e1vka', QUOTE_ID, 130);

  page.text(x + 6, y + 116, 'N\u00e1zov dielu', { size: 6.2, color: [0.42, 0.42, 0.42] });
  page.text(x + 6, y + 138, fittedPartName, { size: partNameFontSize });
  page.text(x + 290, y + 116, 'Typ dokumentu', { size: 6.2, color: [0.42, 0.42, 0.42] });
  page.text(x + 290, y + 138, 'V\u00fdrobn\u00fd v\u00fdkres', { size: 12 });
  page.text(x + 388, y + 116, 'Spracoval', { size: 6.2, color: [0.42, 0.42, 0.42] });
  page.text(x + 388, y + 138, 'Lisonex konfigurátor', { size: 9.2 });
}

function fitRect(
  config: ConfiguratorState,
  box: { x: number; y: number; width: number; height: number },
) {
  const scale = Math.min(box.width / config.widthMm, box.height / config.lengthMm);
  const width = config.widthMm * scale;
  const height = config.lengthMm * scale;
  return {
    scale,
    x: box.x + box.width / 2 - width / 2,
    y: box.y + box.height / 2 - height / 2,
    width,
    height,
  };
}

function plateOutlinePoints(config: ConfiguratorState, rect: ReturnType<typeof fitRect>) {
  const geometry = buildOutlineGeometry(config);
  return geometry.points.map(point => ({
    x: rect.x + (point.xMm / geometry.widthMm) * rect.width,
    y: rect.y + (point.yMm / geometry.lengthMm) * rect.height,
  }));
}

function drawDatumMarker(page: PdfPage, x: number, y: number, label: string) {
  page.fillColor([1, 1, 1]);
  page.strokeColor([0.03, 0.03, 0.03]);
  page.lineWidth(0.65);
  page.rect(x - 7, y - 7, 14, 14, 'B');
  page.text(x, y + 3, label, { size: 8.5, align: 'center' });
}

function drawArrow(
  page: PdfPage,
  x: number,
  y: number,
  direction: 'left' | 'right' | 'up' | 'down',
) {
  const size = 4;
  if (direction === 'left')
    page.polygon(
      [
        { x, y },
        { x: x + size, y: y - size },
        { x: x + size, y: y + size },
      ],
      'f',
    );
  if (direction === 'right')
    page.polygon(
      [
        { x, y },
        { x: x - size, y: y - size },
        { x: x - size, y: y + size },
      ],
      'f',
    );
  if (direction === 'up')
    page.polygon(
      [
        { x, y },
        { x: x - size, y: y + size },
        { x: x + size, y: y + size },
      ],
      'f',
    );
  if (direction === 'down')
    page.polygon(
      [
        { x, y },
        { x: x - size, y: y - size },
        { x: x + size, y: y - size },
      ],
      'f',
    );
}

function drawCornerRadiusCallout(
  page: PdfPage,
  config: ConfiguratorState,
  plate: ReturnType<typeof fitRect>,
  box: { x: number; y: number; width: number; height: number },
  color: Rgb,
) {
  const radiusMm = planCornerRadiusMm(config);
  if (radiusMm <= 0) return;

  const radius = Math.max(3, radiusMm * plate.scale);
  const cornerX = plate.x + plate.width;
  const cornerY = plate.y;
  const arcPoint = {
    x: cornerX - radius * 0.32,
    y: cornerY + radius * 0.32,
  };
  const label = 'R' + compactNumber(radiusMm) + ' mm';
  const labelWidth = Math.max(40, label.length * 5.3 + 12);
  const labelHeight = 18;
  const preferredX = cornerX + 54;
  const labelX = Math.min(preferredX, box.x + box.width - labelWidth / 2 - 6);
  const labelY = Math.max(box.y + 12, cornerY - 20);
  const leaderEndX = labelX - labelWidth / 2;
  const leaderEndY = labelY + labelHeight / 2;

  page.strokeColor(color);
  page.fillColor(color);
  page.lineWidth(0.45);
  page.line(arcPoint.x, arcPoint.y, leaderEndX, leaderEndY);
  page.circle(arcPoint.x, arcPoint.y, 1.7, 'f');
  page.dash([2, 2]);
  page.line(cornerX - radius, cornerY, cornerX - radius, cornerY + radius);
  page.line(cornerX, cornerY + radius, cornerX - radius, cornerY + radius);
  page.dash();

  page.fillColor([1, 1, 1]);
  page.strokeColor(color);
  page.lineWidth(0.45);
  page.rect(labelX - labelWidth / 2, labelY, labelWidth, labelHeight, 'B');
  page.text(labelX, labelY + 12.5, label, {
    size: 8.2,
    align: 'center',
    color,
  });
}

function drawCornerTreatmentCallouts(
  page: PdfPage,
  config: ConfiguratorState,
  plate: ReturnType<typeof fitRect>,
  box: { x: number; y: number; width: number; height: number },
  color: Rgb,
) {
  const rows = getCornerTreatmentRows(config).filter(row => row.type !== 'square');
  if (rows.length === 0) {
    drawCornerRadiusCallout(page, config, plate, box, color);
    return;
  }

  const cornerPoint = (row: CornerTreatmentRow) => {
    const sx = plate.scale * Math.max(row.sizeMm, row.chamferXMm, 0);
    const sy = plate.scale * Math.max(row.sizeMm, row.chamferYMm, 0);
    const insetX = Math.max(4, sx * 0.52);
    const insetY = Math.max(4, sy * 0.52);

    if (row.corner === 'TopLeft') return { x: plate.x + insetX, y: plate.y + insetY };
    if (row.corner === 'TopRight')
      return { x: plate.x + plate.width - insetX, y: plate.y + insetY };
    if (row.corner === 'BottomRight') {
      return { x: plate.x + plate.width - insetX, y: plate.y + plate.height - insetY };
    }
    return { x: plate.x + insetX, y: plate.y + plate.height - insetY };
  };

  const labelPoint = (row: CornerTreatmentRow, labelWidth: number) => {
    const topY = clamp(plate.y - 26, box.y + 4, box.y + box.height - 22);
    const bottomY = clamp(plate.y + plate.height + 12, box.y + 4, box.y + box.height - 20);
    const leftX = clamp(
      plate.x + 28,
      box.x + labelWidth / 2 + 4,
      box.x + box.width - labelWidth / 2 - 4,
    );
    const rightX = clamp(
      plate.x + plate.width - 28,
      box.x + labelWidth / 2 + 4,
      box.x + box.width - labelWidth / 2 - 4,
    );

    if (row.corner === 'TopLeft') return { x: leftX, y: topY };
    if (row.corner === 'TopRight') return { x: rightX, y: topY };
    if (row.corner === 'BottomRight') return { x: rightX, y: bottomY };
    return { x: leftX, y: bottomY };
  };

  rows.forEach(row => {
    const label = `${row.label} ${row.value}`;
    const labelWidth = Math.max(34, label.length * 4.6 + 10);
    const labelHeight = 16;
    const target = cornerPoint(row);
    const labelCenter = labelPoint(row, labelWidth);
    const labelX = labelCenter.x - labelWidth / 2;
    const labelY = labelCenter.y;

    page.strokeColor(color);
    page.fillColor(color);
    page.lineWidth(0.42);
    page.line(target.x, target.y, labelCenter.x, labelY + labelHeight / 2);
    page.circle(target.x, target.y, 1.45, 'f');

    page.fillColor([1, 1, 1]);
    page.strokeColor(color);
    page.lineWidth(0.45);
    page.rect(labelX, labelY, labelWidth, labelHeight, 'B');
    page.text(labelCenter.x, labelY + 11.1, label, {
      size: 6.8,
      align: 'center',
      color,
    });
  });
}

type OpeningCoordinateRow = {
  label: string;
  feature: PartFeature;
  xMm: number;
  yMm: number;
  xLabel: string;
  yLabel: string;
  type: string;
  size: string;
  note: string;
};

type FeatureTableRow = {
  id: string;
  type: string;
  x: string;
  y: string;
  primary: string;
  length: string;
  radius: string;
  angle: string;
};

function featureCoordinateSizeLabel(feature: PartFeature) {
  if (feature.kind === 'roundOpening') return DIAMETER_SYMBOL + compactNumber(feature.diameterMm);
  if (feature.kind === 'slotOpening') {
    return `${compactNumber(feature.widthMm)} ${MULTIPLY_SYMBOL} ${compactNumber(feature.lengthMm)}`;
  }
  if (feature.kind === 'rectOpening') {
    const radius =
      (feature.cornerRadiusMm ?? 0) > 0 ? ` R${compactNumber(feature.cornerRadiusMm ?? 0)}` : '';
    return `${compactNumber(feature.widthMm)} ${MULTIPLY_SYMBOL} ${compactNumber(feature.heightMm)}${radius}`;
  }
  if (feature.kind === 'internalCut') {
    if (feature.shape === 'round') return DIAMETER_SYMBOL + compactNumber(feature.widthMm);
    const radius =
      (feature.cornerRadiusMm ?? 0) > 0 ? ` R${compactNumber(feature.cornerRadiusMm ?? 0)}` : '';
    return `${compactNumber(feature.widthMm)} ${MULTIPLY_SYMBOL} ${compactNumber(feature.lengthMm)}${radius}`;
  }

  return `${compactNumber(feature.widthMm)} ${MULTIPLY_SYMBOL} ${compactNumber(feature.depthMm)}`;
}

function featureSourceKey(feature: PartFeature) {
  return feature.source && typeof feature.sourceIndex === 'number'
    ? `${feature.source}:${feature.sourceIndex}`
    : undefined;
}

function featureDrawingTypeLabel(feature: PartFeature) {
  if (feature.kind === 'roundOpening') return 'Otvor \u00d8';
  if (feature.kind === 'slotOpening') return 'Otvor ov\u00e1l';
  if (feature.kind === 'rectOpening') {
    return isPillRadius(feature.widthMm, feature.heightMm, feature.cornerRadiusMm ?? 0)
      ? 'Otvor ov\u00e1l'
      : 'Otvor obd\u013a.';
  }
  if (feature.kind === 'internalCut') {
    if (feature.shape === 'round') return 'Rez \u00d8';
    if (
      feature.shape === 'slot' ||
      isPillRadius(feature.widthMm, feature.lengthMm, feature.cornerRadiusMm ?? 0)
    ) {
      return 'Rez ov\u00e1l';
    }
    if ((feature.cornerRadiusMm ?? 0) > 0) return 'Rez obl\u00fd';
    return 'Rez obd\u013a.';
  }

  return 'V\u00fdrez obrys';
}

function featureTableTypeLabel(feature: PartFeature) {
  if (feature.kind === 'roundOpening') return 'Otvor';
  if (feature.kind === 'slotOpening') return 'Ov\u00e1l';
  if (feature.kind === 'rectOpening') {
    return isPillRadius(feature.widthMm, feature.heightMm, feature.cornerRadiusMm ?? 0)
      ? 'Ov\u00e1l'
      : 'Obd\u013a.';
  }
  if (feature.kind === 'internalCut') {
    if (feature.shape === 'round') return 'Rez';
    if (
      feature.shape === 'slot' ||
      isPillRadius(feature.widthMm, feature.lengthMm, feature.cornerRadiusMm ?? 0)
    ) {
      return 'Ov\u00e1lny rez';
    }
    if ((feature.cornerRadiusMm ?? 0) > 0) return 'Zaobl. rez';
    return 'Obd\u013a. rez';
  }

  return 'V\u00fdrez';
}

function featureTableRadiusLabel(feature: PartFeature) {
  if (feature.kind === 'slotOpening') return `R${compactNumber(feature.widthMm / 2)}`;
  if (feature.kind === 'rectOpening') {
    return (feature.cornerRadiusMm ?? 0) > 0
      ? `R${compactNumber(feature.cornerRadiusMm ?? 0)}`
      : '-';
  }
  if (feature.kind === 'internalCut') {
    if (feature.shape === 'round') return '-';
    const radius =
      feature.cornerRadiusMm ??
      (feature.shape === 'slot' ? Math.min(feature.widthMm, feature.lengthMm) / 2 : 0);
    return radius > 0 ? `R${compactNumber(radius)}` : '-';
  }
  if (feature.kind === 'edgeCut') {
    return (feature.cornerRadiusMm ?? 0) > 0
      ? `R${compactNumber(feature.cornerRadiusMm ?? 0)}`
      : '-';
  }

  return '-';
}

function featureTableAngleLabel(feature: PartFeature) {
  if (
    feature.kind === 'slotOpening' ||
    feature.kind === 'rectOpening' ||
    feature.kind === 'internalCut'
  ) {
    return Math.abs(feature.angleDeg % 180) > 0.01
      ? `${compactNumber(feature.angleDeg)}${DEGREE_SYMBOL}`
      : '-';
  }

  return '-';
}

function featureTablePrimaryLabel(feature: PartFeature) {
  if (feature.kind === 'roundOpening')
    return `${DIAMETER_SYMBOL}${compactNumber(feature.diameterMm)}`;
  if (
    feature.kind === 'slotOpening' ||
    feature.kind === 'rectOpening' ||
    feature.kind === 'internalCut'
  ) {
    if (feature.kind === 'internalCut' && feature.shape === 'round')
      return `${DIAMETER_SYMBOL}${compactNumber(feature.widthMm)}`;
    return compactNumber(feature.widthMm);
  }
  return compactNumber(feature.widthMm);
}

function featureTableLengthLabel(feature: PartFeature) {
  if (feature.kind === 'roundOpening') return '-';
  if (feature.kind === 'slotOpening') return compactNumber(feature.lengthMm);
  if (feature.kind === 'rectOpening') return compactNumber(feature.heightMm);
  if (feature.kind === 'internalCut') {
    return feature.shape === 'round' ? '-' : compactNumber(feature.lengthMm);
  }
  return compactNumber(feature.depthMm);
}

function toFeatureTableRow(row: OpeningCoordinateRow): FeatureTableRow {
  return {
    id: row.label,
    type: featureTableTypeLabel(row.feature),
    x: row.xLabel,
    y: row.yLabel,
    primary: featureTablePrimaryLabel(row.feature),
    length: featureTableLengthLabel(row.feature),
    radius: featureTableRadiusLabel(row.feature),
    angle: featureTableAngleLabel(row.feature),
  };
}

function edgeCutNote(feature: PartFeature) {
  if (feature.kind !== 'edgeCut') return '';
  const edgeLabels = {
    top: 'horn\u00e1 hrana',
    right: 'prav\u00e1 hrana',
    bottom: 'spodn\u00e1 hrana',
    left: '\u013eav\u00e1 hrana',
  } satisfies Record<string, string>;
  return edgeLabels[feature.edge];
}

function featureAngleNote(feature: PartFeature) {
  if (
    (feature.kind === 'slotOpening' ||
      feature.kind === 'rectOpening' ||
      feature.kind === 'internalCut') &&
    Math.abs(feature.angleDeg % 180) > 0.01
  ) {
    return `uhol ${compactNumber(feature.angleDeg)}${DEGREE_SYMBOL}`;
  }

  return '';
}

function featureStraightPartNote(feature: PartFeature) {
  if (feature.kind === 'slotOpening') {
    return straightPartNote(feature.widthMm, feature.lengthMm, feature.widthMm / 2);
  }

  if (feature.kind === 'rectOpening') {
    return straightPartNote(feature.widthMm, feature.heightMm, feature.cornerRadiusMm ?? 0);
  }

  if (feature.kind === 'internalCut') {
    const radius = feature.cornerRadiusMm ?? (feature.shape === 'slot' ? feature.widthMm / 2 : 0);
    return straightPartNote(feature.widthMm, feature.lengthMm, radius);
  }

  return '';
}

function featureCoordinateNoteLabel(feature: PartFeature) {
  return [edgeCutNote(feature), featureStraightPartNote(feature), featureAngleNote(feature)]
    .filter(Boolean)
    .join('; ');
}

function getFeatureCoordinate(feature: PartFeature, config: ConfiguratorState) {
  if (feature.position) {
    const yFromDatum = Math.round((config.lengthMm - feature.position.yMm) * 1000) / 1000;
    return {
      xMm: Math.round(feature.position.xMm * 1000) / 1000,
      yMm: yFromDatum,
      xLabel: compactNumber(feature.position.xMm),
      yLabel: compactNumber(yFromDatum),
    };
  }

  if (feature.kind !== 'edgeCut') {
    return { xLabel: '-', yLabel: '-' };
  }

  if (feature.edge === 'top') {
    return {
      xMm: Math.round(feature.centerMm * 1000) / 1000,
      yMm: config.lengthMm,
      xLabel: compactNumber(feature.centerMm),
      yLabel: compactNumber(config.lengthMm),
    };
  }

  if (feature.edge === 'bottom') {
    return {
      xMm: Math.round(feature.centerMm * 1000) / 1000,
      yMm: 0,
      xLabel: compactNumber(feature.centerMm),
      yLabel: '0',
    };
  }

  const yFromDatum = Math.round((config.lengthMm - feature.centerMm) * 1000) / 1000;
  return {
    xMm: feature.edge === 'left' ? 0 : config.widthMm,
    yMm: yFromDatum,
    xLabel: feature.edge === 'left' ? '0' : compactNumber(config.widthMm),
    yLabel: compactNumber(yFromDatum),
  };
}

function getOpeningCoordinateRows(config: ConfiguratorState): OpeningCoordinateRow[] {
  const resolvedGeometry = resolvePartGeometry(config);
  let openingIndex = 0;
  let cutIndex = 0;
  let edgeIndex = 0;

  return resolvedGeometry.features
    .filter(feature => feature.enabled)
    .map(feature => {
      const coordinate = getFeatureCoordinate(feature, config);
      const label =
        feature.kind === 'internalCut'
          ? `R${++cutIndex}`
          : feature.kind === 'edgeCut'
            ? `V${++edgeIndex}`
            : `O${++openingIndex}`;

      return {
        label,
        feature,
        ...coordinate,
        xMm: coordinate.xMm ?? 0,
        yMm: coordinate.yMm ?? 0,
        type: featureDrawingTypeLabel(feature),
        size: featureCoordinateSizeLabel(feature),
        note: featureCoordinateNoteLabel(feature),
      };
    });
}

function getDrawingFeatureLabelMap(config: ConfiguratorState) {
  const map = new Map<string, string>();
  getOpeningCoordinateRows(config).forEach(row => {
    const key = featureSourceKey(row.feature);
    if (key) map.set(key, row.label);
  });
  return map;
}

function getFaceCoordinateRows(config: ConfiguratorState) {
  return getOpeningCoordinateRows(config).filter(
    row =>
      row.feature.kind !== 'edgeCut' && typeof row.xMm === 'number' && typeof row.yMm === 'number',
  );
}

function getRoundedSlotStraightGuide(feature: PartFeature) {
  if (feature.kind === 'slotOpening') {
    return {
      angleDeg: feature.angleDeg,
      straightMm: straightPartMm(feature.widthMm, feature.lengthMm, feature.widthMm / 2),
    };
  }

  if (feature.kind === 'rectOpening') {
    const radiusMm = feature.cornerRadiusMm ?? 0;
    if (radiusMm <= 0) return undefined;

    return {
      angleDeg: feature.angleDeg,
      straightMm: straightPartMm(feature.widthMm, feature.heightMm, radiusMm),
    };
  }

  if (feature.kind === 'internalCut') {
    if (feature.shape === 'round') return undefined;
    const radiusMm = feature.cornerRadiusMm ?? (feature.shape === 'slot' ? feature.widthMm / 2 : 0);
    if (radiusMm <= 0) return undefined;

    return {
      angleDeg: feature.angleDeg,
      straightMm: straightPartMm(feature.widthMm, feature.lengthMm, radiusMm),
    };
  }

  return undefined;
}

function getFaceGuideCoordinates(config: ConfiguratorState) {
  const xValues: number[] = [];
  const yValues: number[] = [];

  getFaceCoordinateRows(config).forEach(row => {
    const slotGuide = getRoundedSlotStraightGuide(row.feature);
    if (!slotGuide || slotGuide.straightMm <= 0.01) {
      xValues.push(row.xMm);
      yValues.push(row.yMm);
      return;
    }

    const angleRad = (slotGuide.angleDeg * Math.PI) / 180;
    const isMostlyVertical = Math.abs(Math.sin(angleRad)) > Math.abs(Math.cos(angleRad));
    const straightHalf = slotGuide.straightMm / 2;

    if (isMostlyVertical) {
      xValues.push(row.xMm);
      yValues.push(row.yMm - straightHalf, row.yMm + straightHalf);
      return;
    }

    xValues.push(row.xMm - straightHalf, row.xMm + straightHalf);
    yValues.push(row.yMm);
  });

  return {
    xCoordinates: uniqueSorted(xValues),
    yCoordinates: uniqueSorted(yValues),
  };
}

function edgeCutTargetPoint(
  feature: PartFeature,
  config: ConfiguratorState,
  plate: ReturnType<typeof fitRect>,
) {
  if (feature.kind !== 'edgeCut') return undefined;
  const ratio =
    feature.edge === 'top' || feature.edge === 'bottom'
      ? feature.centerMm / Math.max(config.widthMm, 1)
      : feature.centerMm / Math.max(config.lengthMm, 1);
  const depthPx = Math.max(4, feature.depthMm * plate.scale * 0.52);

  if (feature.edge === 'top') return { x: plate.x + ratio * plate.width, y: plate.y + depthPx };
  if (feature.edge === 'bottom') {
    return { x: plate.x + ratio * plate.width, y: plate.y + plate.height - depthPx };
  }
  if (feature.edge === 'left') return { x: plate.x + depthPx, y: plate.y + ratio * plate.height };
  return { x: plate.x + plate.width - depthPx, y: plate.y + ratio * plate.height };
}

function drawEdgeCutCallouts(
  page: PdfPage,
  config: ConfiguratorState,
  plate: ReturnType<typeof fitRect>,
  box: { x: number; y: number; width: number; height: number },
  color: Rgb,
) {
  getOpeningCoordinateRows(config)
    .filter(row => row.feature.kind === 'edgeCut')
    .forEach((row, index) => {
      const target = edgeCutTargetPoint(row.feature, config, plate);
      if (!target) return;

      const label = row.label;
      const labelWidth = Math.max(24, label.length * 4.4 + 10);
      const labelHeight = 15;
      let labelX = target.x - labelWidth / 2;
      let labelY = target.y - labelHeight / 2;

      if (row.feature.kind === 'edgeCut') {
        if (row.feature.edge === 'top') {
          labelY = plate.y - 22 - (index % 2) * 17;
        } else if (row.feature.edge === 'bottom') {
          labelY = plate.y + plate.height + 10 + (index % 2) * 17;
        } else if (row.feature.edge === 'left') {
          labelX = plate.x - labelWidth - 16;
        } else {
          labelX = plate.x + plate.width + 16;
        }
      }

      labelX = clamp(labelX, box.x + 4, box.x + box.width - labelWidth - 4);
      labelY = clamp(labelY, box.y + 4, box.y + box.height - labelHeight - 4);

      page.strokeColor(color);
      page.fillColor(color);
      page.lineWidth(0.36);
      page.line(target.x, target.y, labelX + labelWidth / 2, labelY + labelHeight / 2);
      page.circle(target.x, target.y, 1.35, 'f');
      page.fillColor([1, 1, 1]);
      page.strokeColor(color);
      page.rect(labelX, labelY, labelWidth, labelHeight, 'B');
      page.text(labelX + labelWidth / 2, labelY + 10.8, label, {
        size: 6.3,
        align: 'center',
        color,
      });
    });
}

function drawFrontView(
  page: PdfPage,
  config: ConfiguratorState,
  box: { x: number; y: number; width: number; height: number },
  technical = true,
  showQuoteDimensions = false,
) {
  const holes = getHoleLayout(config);
  const internalCuts = getProductInternalCuts(config);
  const labelMap = getDrawingFeatureLabelMap(config);
  const selected = getSelectedOptions(config);
  const plate = fitRect(config, box);
  const face = hexToRgb(selected.finish.color);
  const blue: Rgb = [0.08, 0.16, 0.78];
  const axisGray: Rgb = [0.44, 0.44, 0.44];

  if (technical) page.text(box.x, box.y - 16, 'Hlavn\u00fd poh\u013ead', { size: 11 });

  page.fillColor(technical ? [1, 1, 1] : face);
  page.strokeColor([0.04, 0.04, 0.04]);
  page.lineWidth(technical ? 0.9 : 1.1);
  page.polygon(plateOutlinePoints(config, plate), 'B');

  if (technical) {
    drawCornerTreatmentCallouts(page, config, plate, box, blue);
    drawEdgeCutCallouts(page, config, plate, box, blue);
  }

  if (technical) {
    const bendLines = getProductBendLines(config);
    if (bendLines.length > 0) {
      page.strokeColor(blue);
      page.fillColor(blue);
      page.lineWidth(0.4);
      page.dash([8, 4, 2, 4]);
      bendLines.forEach((line, index) => {
        if (line.orientation === 'horizontal') {
          const y = plate.y + (line.positionMm / Math.max(config.lengthMm, 1)) * plate.height;
          page.line(plate.x, y, plate.x + plate.width, y);
          page.text(
            plate.x + plate.width + 8,
            y + 2.4,
            `B${index + 1} ${line.angleDeg}${DEGREE_SYMBOL}`,
            {
              size: 6.8,
              color: blue,
            },
          );
          return;
        }

        const x = plate.x + (line.positionMm / Math.max(config.widthMm, 1)) * plate.width;
        page.line(x, plate.y, x, plate.y + plate.height);
        page.textRotated(
          x - 2,
          plate.y - 8,
          `B${index + 1} ${line.angleDeg}${DEGREE_SYMBOL}`,
          -90,
          {
            size: 6.8,
            color: blue,
          },
        );
      });
      page.dash();
    }
  }

  if (technical) {
    const { xCoordinates, yCoordinates } = getFaceGuideCoordinates(config);
    page.strokeColor(axisGray);
    page.lineWidth(0.24);
    page.dash([9, 4, 2, 4]);
    xCoordinates.forEach(xMm => {
      const x = plate.x + (xMm / config.widthMm) * plate.width;
      page.line(x, plate.y - 10, x, plate.y + plate.height + 10);
    });
    yCoordinates.forEach(yMm => {
      const y = plate.y + plate.height - (yMm / config.lengthMm) * plate.height;
      page.line(plate.x - 10, y, plate.x + plate.width + 10, y);
    });
    page.dash();
  }

  internalCuts.forEach((cut, index) => {
    const cx = plate.x + cut.xRatio * plate.width;
    const cy = plate.y + cut.yRatio * plate.height;
    const renderWidth = cut.lengthMm * plate.scale;
    const renderHeight = cut.widthMm * plate.scale;
    const angleDeg = cut.angleDeg;

    page.fillColor([1, 1, 1]);
    page.strokeColor([0.02, 0.02, 0.02]);
    page.lineWidth(0.68);
    if (cut.shape === 'round') {
      page.circle(cx, cy, Math.max(2.2, renderWidth / 2), 'B');
    } else {
      page.polygon(
        rectPolygonPoints(
          cx,
          cy,
          renderWidth,
          renderHeight,
          angleDeg,
          cut.cornerRadiusMm * plate.scale,
        ),
        'B',
      );
    }
    if (cut.shape !== 'round' && cut.cornerRadiusMm > 0) {
      drawRoundedSlotReferenceLines(
        page,
        cx,
        cy,
        renderWidth,
        renderHeight,
        cut.cornerRadiusMm * plate.scale,
        angleDeg,
        blue,
        mm(straightPartMm(cut.widthMm, cut.lengthMm, cut.cornerRadiusMm)),
      );
    } else {
      drawCenterMark(page, cx, cy, renderWidth, renderHeight, angleDeg, [0.24, 0.24, 0.24]);
    }

    if (technical) {
      const label = labelMap.get(`legacy-internal-cut:${index}`) ?? `R${index + 1}`;
      drawFeatureDimensionTag(page, { x: cx, y: cy }, box, renderWidth, [label], blue);
    }
  });

  holes.forEach((hole, index) => {
    const cx = plate.x + hole.xRatio * plate.width;
    const cy = plate.y + hole.yRatio * plate.height;
    const radius = Math.max(4.2, (hole.diameterMm / 2) * plate.scale);
    const extents = getHoleHalfExtents(hole);
    const renderWidth = Math.max(4.2, extents.xMm * 2 * plate.scale);
    const slotWidth = Math.max(4.2, hole.slotWidthMm * plate.scale);
    const slotLength = Math.max(slotWidth, hole.slotLengthMm * plate.scale);
    page.fillColor([1, 1, 1]);
    page.strokeColor([0.02, 0.02, 0.02]);
    page.lineWidth(0.68);
    if (hole.shape === 'slot') {
      page.polygon(slotPolygonPoints(cx, cy, slotLength, slotWidth, hole.slotAngleDeg), 'B');
    } else if (hole.shape === 'rect') {
      page.polygon(rectPolygonPoints(cx, cy, slotLength, slotWidth, hole.slotAngleDeg), 'B');
    } else {
      page.circle(cx, cy, radius, 'B');
    }
    if (hole.shape === 'slot') {
      drawRoundedSlotReferenceLines(
        page,
        cx,
        cy,
        slotLength,
        slotWidth,
        slotWidth / 2,
        hole.slotAngleDeg,
        blue,
        mm(straightPartMm(hole.slotWidthMm, hole.slotLengthMm, hole.slotWidthMm / 2)),
      );
    } else {
      drawCenterMark(
        page,
        cx,
        cy,
        hole.shape === 'round' ? radius * 2 : slotLength,
        hole.shape === 'round' ? radius * 2 : slotWidth,
        hole.shape === 'round' ? 0 : hole.slotAngleDeg,
        [0.24, 0.24, 0.24],
      );
    }

    if (technical) {
      const label = labelMap.get(`legacy-hole:${index}`) ?? `O${index + 1}`;
      drawFeatureDimensionTag(page, { x: cx, y: cy }, box, renderWidth, [label], blue);
    }
  });

  if (!technical && !showQuoteDimensions) return;

  page.strokeColor(blue);
  page.fillColor(blue);
  page.lineWidth(0.42);
  const bottomDimY = plate.y + plate.height + (technical ? 42 : 15);
  const leftDimX = plate.x - (technical ? 44 : 16);
  const dimensionSize = technical ? 9.2 : 7.2;

  if (!technical) {
    page.line(plate.x, bottomDimY, plate.x + plate.width, bottomDimY);
    page.line(plate.x, plate.y + plate.height, plate.x, bottomDimY + 5);
    page.line(plate.x + plate.width, plate.y + plate.height, plate.x + plate.width, bottomDimY + 5);
    drawArrow(page, plate.x, bottomDimY, 'right');
    drawArrow(page, plate.x + plate.width, bottomDimY, 'left');
    page.text(plate.x + plate.width / 2, bottomDimY + 13, mm(config.widthMm), {
      size: dimensionSize,
      align: 'center',
      color: blue,
    });

    page.line(leftDimX, plate.y, leftDimX, plate.y + plate.height);
    page.line(leftDimX - 5, plate.y, plate.x, plate.y);
    page.line(leftDimX - 5, plate.y + plate.height, plate.x, plate.y + plate.height);
    drawArrow(page, leftDimX, plate.y, 'down');
    drawArrow(page, leftDimX, plate.y + plate.height, 'up');
    page.textRotated(leftDimX - 10, plate.y + plate.height / 2, mm(config.lengthMm), -90, {
      size: dimensionSize,
      align: 'center',
      color: blue,
    });
    return;
  }

  const holeXDimY = plate.y + plate.height + 12;
  const holeYDimX = plate.x - 16;
  const { xCoordinates, yCoordinates } = getFaceGuideCoordinates(config);

  page.strokeColor(blue);
  page.fillColor(blue);
  page.lineWidth(0.22);
  page.dash([2, 3]);
  xCoordinates.forEach(xMm => {
    const x = plate.x + (xMm / config.widthMm) * plate.width;
    page.line(x, plate.y, x, holeXDimY + 4);
  });
  yCoordinates.forEach(yMm => {
    const y = plate.y + plate.height - (yMm / config.lengthMm) * plate.height;
    page.line(holeYDimX, y, plate.x + plate.width, y);
  });
  page.dash();
  const xLabelOffsets = staggerCloseLabels(
    xCoordinates.map(xMm => ({
      key: xMm,
      position: plate.x + (xMm / config.widthMm) * plate.width,
    })),
  );
  xCoordinates.forEach(xMm => {
    const x = plate.x + (xMm / config.widthMm) * plate.width;
    const labelOffset = xLabelOffsets.get(xMm) ?? 0;
    if (Math.abs(labelOffset) > 0.1) {
      page.strokeColor(blue);
      page.lineWidth(0.18);
      page.line(x, holeXDimY + 5.5, x + labelOffset, holeXDimY + 5.5);
    }
    page.textRotated(x - 2.2 + labelOffset, holeXDimY + 8, compactNumber(xMm), -90, {
      size: 6.8,
      align: 'left',
      color: blue,
    });
  });
  yCoordinates.forEach(yMm => {
    const y = plate.y + plate.height - (yMm / config.lengthMm) * plate.height;
    page.text(holeYDimX - 1, y + 2.4, compactNumber(yMm), {
      size: 6.8,
      align: 'right',
      color: blue,
    });
  });

  page.line(plate.x, bottomDimY, plate.x + plate.width, bottomDimY);
  page.line(plate.x, plate.y + plate.height, plate.x, bottomDimY + 8);
  page.line(plate.x + plate.width, plate.y + plate.height, plate.x + plate.width, bottomDimY + 8);
  drawArrow(page, plate.x, bottomDimY, 'right');
  drawArrow(page, plate.x + plate.width, bottomDimY, 'left');
  page.text(plate.x + plate.width / 2, bottomDimY + 19, mm(config.widthMm), {
    size: dimensionSize,
    align: 'center',
    color: blue,
  });

  page.line(leftDimX, plate.y, leftDimX, plate.y + plate.height);
  page.line(leftDimX - 8, plate.y, plate.x, plate.y);
  page.line(leftDimX - 8, plate.y + plate.height, plate.x, plate.y + plate.height);
  drawArrow(page, leftDimX, plate.y, 'down');
  drawArrow(page, leftDimX, plate.y + plate.height, 'up');
  page.text(leftDimX - 12, plate.y + plate.height / 2, mm(config.lengthMm), {
    size: dimensionSize,
    align: 'right',
    color: blue,
  });

  page.strokeColor([0.03, 0.03, 0.03]);
  page.fillColor([0.03, 0.03, 0.03]);
  page.lineWidth(0.5);
  page.line(plate.x + 24, plate.y + plate.height + 25, plate.x + 24, plate.y + plate.height);
  drawDatumMarker(page, plate.x + 24, plate.y + plate.height + 38, 'A');
  page.line(plate.x - 26, plate.y + plate.height - 24, plate.x, plate.y + plate.height - 24);
  drawDatumMarker(page, plate.x - 39, plate.y + plate.height - 24, 'B');
}

function drawSideView(
  page: PdfPage,
  config: ConfiguratorState,
  box: { x: number; y: number; width: number; height: number },
  showTitle = true,
) {
  const allBendLines = getProductBendLines(config);
  const verticalBends = allBendLines.filter(line => line.orientation === 'vertical');
  const horizontalBends = allBendLines.filter(line => line.orientation === 'horizontal');
  const verticalScore = verticalBends.reduce((sum, line) => sum + Math.abs(line.angleDeg), 0);
  const horizontalScore = horizontalBends.reduce((sum, line) => sum + Math.abs(line.angleDeg), 0);
  const preferredOrientation =
    verticalBends.length > 0 && horizontalBends.length > 0
      ? verticalScore >= horizontalScore
        ? 'vertical'
        : 'horizontal'
      : horizontalBends.length > 0
        ? 'horizontal'
        : 'vertical';
  const bendLines = allBendLines
    .filter(line => line.orientation === preferredOrientation)
    .sort((a, b) => a.positionMm - b.positionMm);
  const bendAxisLength = preferredOrientation === 'horizontal' ? config.lengthMm : config.widthMm;

  if (bendLines.length > 0) {
    if (showTitle) {
      page.text(box.x, box.y - 8, 'Ohnutý profil dielu', {
        size: 9.2,
        color: [0.32, 0.32, 0.32],
      });
    }

    const breakpoints = [0, ...bendLines.map(line => line.positionMm), bendAxisLength];
    const pointsMm: Point[] = [{ x: 0, y: 0 }];
    let direction = { x: 0, y: 1 };
    let current = { x: 0, y: 0 };

    for (let index = 0; index < breakpoints.length - 1; index += 1) {
      const segmentLength = Math.max(0, breakpoints[index + 1] - breakpoints[index]);
      current = {
        x: current.x + direction.x * segmentLength,
        y: current.y + direction.y * segmentLength,
      };
      pointsMm.push(current);

      const bend = bendLines[index];
      if (bend) {
        const sign = bend.direction === 'down' ? -1 : 1;
        const angle = (bend.angleDeg * sign * Math.PI) / 180;
        direction = {
          x: direction.x * Math.cos(angle) - direction.y * Math.sin(angle),
          y: direction.x * Math.sin(angle) + direction.y * Math.cos(angle),
        };
      }
    }

    const minX = Math.min(...pointsMm.map(point => point.x));
    const maxX = Math.max(...pointsMm.map(point => point.x));
    const minY = Math.min(...pointsMm.map(point => point.y));
    const maxY = Math.max(...pointsMm.map(point => point.y));
    const scale = Math.min(
      (box.width * 0.72) / Math.max(maxX - minX, config.thicknessMm, 1),
      (box.height * 0.72) / Math.max(maxY - minY, config.thicknessMm, 1),
    );
    const offsetX = box.x + box.width / 2 - ((minX + maxX) / 2) * scale;
    const offsetY = box.y + box.height / 2 - ((minY + maxY) / 2) * scale;
    const points = pointsMm.map(point => ({
      x: offsetX + point.x * scale,
      y: offsetY + point.y * scale,
    }));

    page.strokeColor([0.05, 0.05, 0.05]);
    page.lineWidth(Math.max(2.2, config.thicknessMm * scale));
    for (let index = 0; index < points.length - 1; index += 1) {
      page.line(points[index].x, points[index].y, points[index + 1].x, points[index + 1].y);
    }
    page.strokeColor([0.08, 0.16, 0.78]);
    page.fillColor([0.08, 0.16, 0.78]);
    page.lineWidth(0.36);
    bendLines.forEach((line, index) => {
      const point = points[index + 1];
      if (!point) return;
      page.circle(point.x, point.y, 2.2, 'B');
      page.text(point.x + 5, point.y - 5, `${line.angleDeg}${DEGREE_SYMBOL}`, {
        size: 6.8,
        color: [0.08, 0.16, 0.78],
      });
    });
    return;
  }

  const height = Math.min(box.height * 0.76, Math.max(44, config.lengthMm * 0.16));
  const thickness = Math.max(6, Math.min(22, config.thicknessMm * 1.8));
  const x = box.x + box.width / 2 - thickness / 2;
  const y = box.y + box.height / 2 - height / 2;
  if (showTitle) {
    page.text(box.x, box.y - 8, 'Bo\u010dn\u00fd poh\u013ead', {
      size: 9.2,
      color: [0.32, 0.32, 0.32],
    });
  }
  page.fillColor([1, 1, 1]);
  page.strokeColor([0.05, 0.05, 0.05]);
  page.lineWidth(0.72);
  page.rect(x, y, thickness, height, 'B');

  page.strokeColor([0.08, 0.16, 0.78]);
  page.fillColor([0.08, 0.16, 0.78]);
  page.lineWidth(0.38);
  const dimY = y + height + 18;
  page.line(x - 16, dimY, x + thickness + 16, dimY);
  page.line(x, y + height + 7, x, dimY + 8);
  page.line(x + thickness, y + height + 7, x + thickness, dimY + 8);
  drawArrow(page, x, dimY, 'right');
  drawArrow(page, x + thickness, dimY, 'left');
  page.text(x + thickness / 2, dimY + 15, mm(config.thicknessMm), {
    size: 8.4,
    align: 'center',
    color: [0.08, 0.16, 0.78],
  });
}

function drawIsoView(
  page: PdfPage,
  config: ConfiguratorState,
  box: { x: number; y: number; width: number; height: number },
) {
  const selected = getSelectedOptions(config);
  const geometry = resolvePartGeometry(config);
  const facePaths = [
    ...geometry.openingPaths,
    ...geometry.cutPaths.filter(path => path.role === 'internalCut'),
  ];
  const scale = Math.min(
    (box.width * 0.66) / Math.max(config.widthMm, 1),
    (box.height * 0.72) / Math.max(config.lengthMm, 1),
  );
  const xAxis = { x: 0.78 * scale, y: 0.22 * scale };
  const yAxis = { x: 0, y: 1 * scale };
  const depth = Math.max(10, Math.min(34, config.thicknessMm * 2.6));
  const dv = { x: depth, y: -depth * 0.72 };
  const rawProject = (point: { xMm: number; yMm: number }): Point => ({
    x: point.xMm * xAxis.x + point.yMm * yAxis.x,
    y: point.xMm * xAxis.y + point.yMm * yAxis.y,
  });
  const rawOutline = geometry.finalOutlinePath.points.map(rawProject);
  const allRawPoints = [
    ...rawOutline,
    ...rawOutline.map(point => ({ x: point.x + dv.x, y: point.y + dv.y })),
  ];
  const minX = Math.min(...allRawPoints.map(point => point.x));
  const maxX = Math.max(...allRawPoints.map(point => point.x));
  const minY = Math.min(...allRawPoints.map(point => point.y));
  const maxY = Math.max(...allRawPoints.map(point => point.y));
  const offset = {
    x: box.x + box.width / 2 - (minX + maxX) / 2,
    y: box.y + box.height / 2 - (minY + maxY) / 2,
  };
  const project = (point: { xMm: number; yMm: number }): Point => {
    const raw = rawProject(point);
    return { x: offset.x + raw.x, y: offset.y + raw.y };
  };
  const frontOutline = geometry.finalOutlinePath.points.map(project);
  const backOutline = frontOutline.map(point => ({ x: point.x + dv.x, y: point.y + dv.y }));

  page.fillColor(shadeHex(selected.finish.color, -0.22));
  page.strokeColor([0.12, 0.12, 0.12]);
  page.lineWidth(0.58);
  for (let index = 0; index < frontOutline.length; index += 1) {
    const nextIndex = (index + 1) % frontOutline.length;
    const frontA = frontOutline[index];
    const frontB = frontOutline[nextIndex];
    const backA = backOutline[index];
    const backB = backOutline[nextIndex];
    if (!frontA || !frontB || !backA || !backB) continue;
    page.polygon([frontA, frontB, backB, backA], 'B');
  }

  page.fillColor(shadeHex(selected.finish.color, -0.42));
  page.polygon(backOutline, 'B');
  page.fillColor(hexToRgb(selected.finish.color));
  page.strokeColor([0.04, 0.04, 0.04]);
  page.lineWidth(0.88);
  page.polygon(frontOutline, 'B');

  facePaths.forEach(path => {
    const projected = path.points.map(project);
    if (projected.length < 3) return;
    page.fillColor([1, 1, 1]);
    page.strokeColor([0.05, 0.05, 0.05]);
    page.lineWidth(0.62);
    page.polygon(projected, 'B');
  });

  page.strokeColor([0.24, 0.24, 0.24]);
  page.lineWidth(0.34);
  frontOutline.forEach((point, index) => {
    if (index % 8 !== 0) return;
    page.line(point.x, point.y, point.x + dv.x, point.y + dv.y);
  });
}

function fitImageIntoBox(
  image: PdfJpegImage,
  box: { x: number; y: number; width: number; height: number },
) {
  const scale = Math.min(box.width / image.width, box.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    x: box.x + box.width / 2 - width / 2,
    y: box.y + box.height / 2 - height / 2,
    width,
    height,
  };
}

function drawJpegImageContain(
  page: PdfPage,
  name: string,
  image: PdfJpegImage,
  box: { x: number; y: number; width: number; height: number },
) {
  const fitted = fitImageIntoBox(image, box);
  page.image(name, fitted.x, fitted.y, fitted.width, fitted.height);
}

type FoldPoint3D = { u: number; v: number; z: number };

function drawFoldedPlatePreview(
  page: PdfPage,
  config: ConfiguratorState,
  box: { x: number; y: number; width: number; height: number },
) {
  const bendLines = getProductBendLines(config).filter(line => line.angleDeg > 0);
  if (bendLines.length === 0) return false;

  const verticalBends = bendLines.filter(line => line.orientation === 'vertical');
  const horizontalBends = bendLines.filter(line => line.orientation === 'horizontal');
  const verticalScore = verticalBends.reduce((sum, line) => sum + Math.abs(line.angleDeg), 0);
  const horizontalScore = horizontalBends.reduce((sum, line) => sum + Math.abs(line.angleDeg), 0);
  const preferredOrientation =
    verticalBends.length > 0 && horizontalBends.length > 0
      ? verticalScore >= horizontalScore
        ? 'vertical'
        : 'horizontal'
      : horizontalBends.length > 0
        ? 'horizontal'
        : 'vertical';
  const axisLimit = preferredOrientation === 'horizontal' ? config.lengthMm : config.widthMm;
  const primaryBend = bendLines
    .filter(line => line.orientation === preferredOrientation)
    .sort(
      (a, b) => Math.abs(a.positionMm - axisLimit / 2) - Math.abs(b.positionMm - axisLimit / 2),
    )[0];
  if (!primaryBend) return false;

  const axisLength = preferredOrientation === 'horizontal' ? config.widthMm : config.lengthMm;
  const foldLength = Math.max(1, Math.min(primaryBend.positionMm, axisLimit));
  const flatLength = Math.max(1, axisLimit - foldLength);
  const foldSign = primaryBend.direction === 'down' ? -1 : 1;
  const uAxis = { x: 0.82, y: -0.16 };
  const vAxis = { x: -0.32, y: 0.44 };
  const zAxis = { x: 0.18, y: -0.68 };
  const baseProject = (point: FoldPoint3D): Point => ({
    x: point.u * uAxis.x + point.v * vAxis.x + point.z * zAxis.x,
    y: point.u * uAxis.y + point.v * vAxis.y + point.z * zAxis.y,
  });
  const flatPanel: FoldPoint3D[] = [
    { u: 0, v: 0, z: 0 },
    { u: axisLength, v: 0, z: 0 },
    { u: axisLength, v: flatLength, z: 0 },
    { u: 0, v: flatLength, z: 0 },
  ];
  const foldedPanel: FoldPoint3D[] = [
    { u: 0, v: 0, z: 0 },
    { u: axisLength, v: 0, z: 0 },
    { u: axisLength, v: 0, z: foldLength * foldSign },
    { u: 0, v: 0, z: foldLength * foldSign },
  ];
  const rawPoints = [...flatPanel, ...foldedPanel].map(baseProject);
  const minX = Math.min(...rawPoints.map(point => point.x));
  const maxX = Math.max(...rawPoints.map(point => point.x));
  const minY = Math.min(...rawPoints.map(point => point.y));
  const maxY = Math.max(...rawPoints.map(point => point.y));
  const scale = Math.min(
    (box.width - 30) / Math.max(maxX - minX, 1),
    (box.height - 22) / Math.max(maxY - minY, 1),
  );
  const offsetX = box.x + box.width / 2 - ((minX + maxX) / 2) * scale;
  const offsetY = box.y + box.height / 2 - ((minY + maxY) / 2) * scale;
  const project = (point: FoldPoint3D): Point => {
    const raw = baseProject(point);
    return {
      x: offsetX + raw.x * scale,
      y: offsetY + raw.y * scale,
    };
  };
  const selected = getSelectedOptions(config);
  const flatColor = shadeHex(selected.finish.color, selected.finish.id === 'black' ? 0.16 : 0.02);
  const foldColor = shadeHex(selected.finish.color, selected.finish.id === 'black' ? 0.3 : 0.18);
  const sideColor = shadeHex(selected.finish.color, -0.32);

  page.fillColor(foldColor);
  page.strokeColor([0.04, 0.04, 0.04]);
  page.lineWidth(0.72);
  page.polygon(foldedPanel.map(project), 'B');

  page.fillColor(flatColor);
  page.polygon(flatPanel.map(project), 'B');

  const thickness = Math.max(3, Math.min(9, config.thicknessMm * scale * 0.6));
  const flatEdgeA = project({ u: 0, v: flatLength, z: 0 });
  const flatEdgeB = project({ u: axisLength, v: flatLength, z: 0 });
  page.strokeColor(sideColor);
  page.lineWidth(thickness);
  page.line(flatEdgeA.x, flatEdgeA.y, flatEdgeB.x, flatEdgeB.y);

  const hingeA = project({ u: 0, v: 0, z: 0 });
  const hingeB = project({ u: axisLength, v: 0, z: 0 });
  page.strokeColor([0.08, 0.16, 0.78]);
  page.fillColor([0.08, 0.16, 0.78]);
  page.lineWidth(0.56);
  page.dash([5, 3]);
  page.line(hingeA.x, hingeA.y, hingeB.x, hingeB.y);
  page.dash();
  page.text(
    (hingeA.x + hingeB.x) / 2 + 4,
    (hingeA.y + hingeB.y) / 2 - 5,
    `${primaryBend.angleDeg}${DEGREE_SYMBOL}`,
    {
      size: 7,
      color: [0.08, 0.16, 0.78],
    },
  );

  const openingCenters: Array<{ point: FoldPoint3D; sizeMm: number }> = [
    ...getHoleLayout(config).map(hole => ({
      point:
        preferredOrientation === 'horizontal'
          ? {
              u: hole.xMm,
              v: hole.yMm >= primaryBend.positionMm ? hole.yMm - primaryBend.positionMm : 0,
              z:
                hole.yMm < primaryBend.positionMm
                  ? (primaryBend.positionMm - hole.yMm) * foldSign
                  : 0,
            }
          : {
              u: hole.yMm,
              v: hole.xMm >= primaryBend.positionMm ? hole.xMm - primaryBend.positionMm : 0,
              z:
                hole.xMm < primaryBend.positionMm
                  ? (primaryBend.positionMm - hole.xMm) * foldSign
                  : 0,
            },
      sizeMm: hole.shape === 'round' ? hole.diameterMm : Math.max(hole.slotWidthMm, 1),
    })),
    ...getProductInternalCuts(config).map(cut => ({
      point:
        preferredOrientation === 'horizontal'
          ? {
              u: cut.xMm,
              v: cut.yMm >= primaryBend.positionMm ? cut.yMm - primaryBend.positionMm : 0,
              z:
                cut.yMm < primaryBend.positionMm
                  ? (primaryBend.positionMm - cut.yMm) * foldSign
                  : 0,
            }
          : {
              u: cut.yMm,
              v: cut.xMm >= primaryBend.positionMm ? cut.xMm - primaryBend.positionMm : 0,
              z:
                cut.xMm < primaryBend.positionMm
                  ? (primaryBend.positionMm - cut.xMm) * foldSign
                  : 0,
            },
      sizeMm: cut.shape === 'round' ? cut.widthMm : Math.max(cut.widthMm, 1),
    })),
  ];

  openingCenters.forEach(opening => {
    const center = project(opening.point);
    const radius = Math.max(1.8, Math.min(7, opening.sizeMm * scale * 0.22));
    page.fillColor([1, 1, 1]);
    page.strokeColor([0.08, 0.08, 0.08]);
    page.lineWidth(0.52);
    page.ellipse(center.x, center.y, radius, radius * 0.66, 'B');
  });

  return true;
}

function drawBentPreview(
  page: PdfPage,
  pdf: PdfDocument,
  config: ConfiguratorState,
  images: BentPreviewImage[] | undefined,
  box: { x: number; y: number; width: number; height: number },
) {
  page.text(box.x, box.y - 12, 'Ohnutý 3D pohľad', {
    size: 10.2,
    color: [0.24, 0.24, 0.24],
  });

  page.strokeColor([0.82, 0.82, 0.82]);
  page.fillColor([1, 1, 1]);
  page.lineWidth(0.42);
  page.rect(box.x, box.y, box.width, box.height, 'B');

  const availableImages = images?.filter(item => item.image) ?? [];
  if (availableImages.length > 0) {
    const gap = 6;
    const itemWidth = (box.width - 12 - gap * 2) / 3;
    const itemHeight = box.height - 18;

    availableImages.slice(0, 3).forEach((item, index) => {
      if (!item.image) return;

      const itemX = box.x + 6 + index * (itemWidth + gap);
      const imageName = pdf.addJpegImage(item.image);
      drawJpegImageContain(page, imageName, item.image, {
        x: itemX + 2,
        y: box.y + 8,
        width: itemWidth - 4,
        height: itemHeight - 8,
      });
    });
    return;
  }

  if (
    drawFoldedPlatePreview(page, config, {
      x: box.x + 8,
      y: box.y + 8,
      width: box.width - 16,
      height: box.height - 16,
    })
  ) {
    return;
  }

  drawSideView(
    page,
    config,
    {
      x: box.x + box.width * 0.28,
      y: box.y + box.height * 0.22,
      width: box.width * 0.44,
      height: box.height * 0.58,
    },
    false,
  );
}

function dataUrlToJpegImage(dataUrl: string, width: number, height: number): PdfJpegImage {
  const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');
  const binary = window.atob(base64);
  const data = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    data[index] = binary.charCodeAt(index);
  }
  return { data, width, height };
}

function cropCanvasToContent(source: HTMLCanvasElement, paddingPx = 24): HTMLCanvasElement {
  const width = source.width;
  const height = source.height;
  const working = document.createElement('canvas');
  working.width = width;
  working.height = height;
  const context = working.getContext('2d', { willReadFrequently: true });
  if (!context) return source;

  context.drawImage(source, 0, 0);
  const pixels = context.getImageData(0, 0, width, height).data;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const r = pixels[offset] ?? 255;
      const g = pixels[offset + 1] ?? 255;
      const b = pixels[offset + 2] ?? 255;
      const alpha = pixels[offset + 3] ?? 255;
      if (alpha > 8 && (r < 246 || g < 246 || b < 246)) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < minX || maxY < minY) return source;

  const sourceX = Math.max(0, minX - paddingPx);
  const sourceY = Math.max(0, minY - paddingPx);
  const sourceWidth = Math.min(width - sourceX, maxX - minX + 1 + paddingPx * 2);
  const sourceHeight = Math.min(height - sourceY, maxY - minY + 1 + paddingPx * 2);
  const cropped = document.createElement('canvas');
  cropped.width = sourceWidth;
  cropped.height = sourceHeight;
  const croppedContext = cropped.getContext('2d');
  if (!croppedContext) return source;

  croppedContext.fillStyle = '#ffffff';
  croppedContext.fillRect(0, 0, sourceWidth, sourceHeight);
  croppedContext.drawImage(
    working,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    sourceWidth,
    sourceHeight,
  );

  return cropped;
}

function disposeThreeObject(object: import('three').Object3D) {
  object.traverse(child => {
    const mesh = child as import('three').Mesh;
    mesh.geometry?.dispose();
    const material = mesh.material;
    if (Array.isArray(material)) material.forEach(item => item.dispose());
    else material?.dispose();
  });
}

function centerThreeObject(THREE: ThreeModule, object: import('three').Object3D) {
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  object.position.sub(center);
  object.updateMatrixWorld(true);
  return Math.max(size.x, size.y, size.z, 120);
}

function frameThreeCamera(
  camera: import('three').OrthographicCamera,
  maxDim: number,
  aspect: number,
  padding = 1.22,
) {
  const viewHeight = maxDim * padding;
  const viewWidth = viewHeight * Math.max(aspect, 1);
  camera.left = -viewWidth / 2;
  camera.right = viewWidth / 2;
  camera.top = viewHeight / 2;
  camera.bottom = -viewHeight / 2;
  camera.near = -maxDim * 8;
  camera.far = maxDim * 8;
  camera.updateProjectionMatrix();
}

function centerThreeCameraOnObject(
  THREE: ThreeModule,
  camera: import('three').OrthographicCamera,
  object: import('three').Object3D,
) {
  object.updateMatrixWorld(true);
  camera.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  const corners = [
    new THREE.Vector3(box.min.x, box.min.y, box.min.z),
    new THREE.Vector3(box.min.x, box.min.y, box.max.z),
    new THREE.Vector3(box.min.x, box.max.y, box.min.z),
    new THREE.Vector3(box.min.x, box.max.y, box.max.z),
    new THREE.Vector3(box.max.x, box.min.y, box.min.z),
    new THREE.Vector3(box.max.x, box.min.y, box.max.z),
    new THREE.Vector3(box.max.x, box.max.y, box.min.z),
    new THREE.Vector3(box.max.x, box.max.y, box.max.z),
  ];
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  corners.forEach(corner => {
    const projected = corner.project(camera);
    minX = Math.min(minX, projected.x);
    maxX = Math.max(maxX, projected.x);
    minY = Math.min(minY, projected.y);
    maxY = Math.max(maxY, projected.y);
  });

  if (![minX, maxX, minY, maxY].every(Number.isFinite)) return;
  const offsetX = (minX + maxX) / 2;
  const offsetY = (minY + maxY) / 2;
  const viewWidth = camera.right - camera.left;
  const viewHeight = camera.top - camera.bottom;
  camera.left += (offsetX * viewWidth) / 2;
  camera.right += (offsetX * viewWidth) / 2;
  camera.top += (offsetY * viewHeight) / 2;
  camera.bottom += (offsetY * viewHeight) / 2;
  camera.updateProjectionMatrix();
}

function createThreePreviewReferenceGrid(
  THREE: ThreeModule,
  modelSize: number,
): import('three').Object3D {
  const group = new THREE.Group();
  const size = Math.max(180, modelSize * 1.65);
  const half = size / 2;
  const divisions = 16;
  const step = size / divisions;
  const minorPositions: number[] = [];
  const majorPositions: number[] = [];

  for (let index = 0; index <= divisions; index += 1) {
    const position = -half + index * step;
    const bucket = index % 4 === 0 ? majorPositions : minorPositions;
    bucket.push(-half, position, 0, half, position, 0);
    bucket.push(position, -half, 0, position, half, 0);
  }

  const makeLines = (positions: number[], color: number, opacity: number) => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthWrite: false,
    });
    return new THREE.LineSegments(geometry, material);
  };

  group.add(makeLines(minorPositions, 0xeeeeee, 0.62));
  group.add(makeLines(majorPositions, 0xdedede, 0.72));
  group.position.z = -Math.max(1, modelSize * 0.12);
  return group;
}

type ThreePreviewRenderOptions = {
  applyBends?: boolean;
  showBendGuides?: boolean;
  hideBendGuidesWhenApplied?: boolean;
  showReferenceGrid?: boolean;
  cropToContent?: boolean;
  cropPaddingPx?: number;
  width?: number;
  height?: number;
  yaw?: number;
  pitch?: number;
  padding?: number;
};

type BentPreviewImage = {
  label: string;
  image?: PdfJpegImage;
};

async function renderThreePreviewImage(
  config: ConfiguratorState,
  options: ThreePreviewRenderOptions = {},
): Promise<PdfJpegImage | undefined> {
  if (typeof window === 'undefined') return undefined;

  try {
    const THREE = await import('three');
    const { buildSceneObjects } = await import('../plate-preview-3d');
    const width = options.width ?? 960;
    const height = options.height ?? 640;
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -5000, 5000);
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true,
    });

    renderer.setPixelRatio(1);
    renderer.setSize(width, height, false);
    renderer.setClearColor(0xffffff, 1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;

    scene.add(new THREE.HemisphereLight('#ffffff', '#d6d6d2', 2.25));
    const keyLight = new THREE.DirectionalLight('#ffffff', 2.15);
    keyLight.position.set(-280, -330, 520);
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight('#f5f0ea', 1.05);
    rimLight.position.set(360, 220, 260);
    scene.add(rimLight);

    const model = buildSceneObjects(THREE, config, {
      applyBends: options.applyBends,
      showBendGuides: options.showBendGuides,
      hideBendGuidesWhenApplied: options.hideBendGuidesWhenApplied,
      documentRender: true,
    });
    scene.add(model);
    const modelSize = centerThreeObject(THREE, model);
    const referenceGrid = options.showReferenceGrid
      ? createThreePreviewReferenceGrid(THREE, modelSize)
      : undefined;
    if (referenceGrid) {
      scene.add(referenceGrid);
    }
    const distance = modelSize * 3.4;
    const yaw = options.yaw ?? 0.72;
    const pitch = options.pitch ?? 0.45;
    camera.position.set(
      Math.sin(yaw) * Math.cos(pitch) * distance,
      Math.cos(yaw) * Math.cos(pitch) * distance,
      Math.sin(pitch) * distance,
    );
    camera.lookAt(0, 0, 0);
    frameThreeCamera(camera, modelSize, width / height, options.padding ?? 1.38);
    centerThreeCameraOnObject(THREE, camera, model);
    centerThreeCameraOnObject(THREE, camera, model);

    renderer.render(scene, camera);
    const outputCanvas = options.cropToContent
      ? cropCanvasToContent(renderer.domElement, options.cropPaddingPx ?? 24)
      : renderer.domElement;
    const dataUrl = outputCanvas.toDataURL('image/jpeg', 0.94);
    disposeThreeObject(model);
    if (referenceGrid) {
      disposeThreeObject(referenceGrid);
    }
    renderer.dispose();
    return dataUrlToJpegImage(dataUrl, outputCanvas.width, outputCanvas.height);
  } catch {
    return undefined;
  }
}

async function renderIsoPreviewImage(config: ConfiguratorState): Promise<PdfJpegImage | undefined> {
  return renderThreePreviewImage(config);
}

async function renderBentPreviewImage(config: ConfiguratorState): Promise<BentPreviewImage[]> {
  const baseOptions = {
    applyBends: true,
    hideBendGuidesWhenApplied: true,
    showBendGuides: false,
    showReferenceGrid: true,
    cropToContent: false,
    width: 540,
    height: 420,
    padding: 1.5,
  } satisfies ThreePreviewRenderOptions;
  const views = [
    { label: 'Spredu 45\u00b0', yaw: 0.28, pitch: 0.42 },
    { label: 'Zhora 45\u00b0', yaw: 0.82, pitch: 0.96 },
    { label: 'Zboku 45\u00b0', yaw: 1.72, pitch: 0.42 },
  ];

  return Promise.all(
    views.map(async view => ({
      label: view.label,
      image: await renderThreePreviewImage(config, {
        ...baseOptions,
        yaw: view.yaw,
        pitch: view.pitch,
      }),
    })),
  );
}

function drawFeatureCoordinateTable(
  page: PdfPage,
  config: ConfiguratorState,
  box: { x: number; y: number; width: number; height: number },
) {
  const rows = getOpeningCoordinateRows(config).map(toFeatureTableRow);
  const headerHeight = 20;
  const availableRowsHeight = box.height - headerHeight - 20;
  const rowHeight = rows.length
    ? Math.max(9.6, Math.min(17, availableRowsHeight / rows.length))
    : 15;
  const baseColWidths = [32, 64, 43, 43, 42, 45, 36, 38];
  const tableWidth = Math.min(
    box.width,
    baseColWidths.reduce((sum, width) => sum + width, 0),
  );
  const colScale = tableWidth / baseColWidths.reduce((sum, width) => sum + width, 0);
  const colWidths = baseColWidths.map(width => width * colScale);
  const tableHeight = headerHeight + Math.max(rows.length, 1) * rowHeight;
  const tableX = box.x + box.width / 2 - tableWidth / 2;
  const tableY = box.y + 8;
  const blue: Rgb = [0.08, 0.16, 0.78];
  const bodyFontSize = rowHeight < 11 ? 5.8 : rowHeight < 14 ? 6.5 : 7.4;
  const markerRadius = Math.min(6.4, Math.max(3.2, rowHeight * 0.34));

  page.text(box.x, box.y - 15, 'Rezy a otvory', { size: 11.2 });

  page.strokeColor([0.48, 0.48, 0.48]);
  page.fillColor([1, 1, 1]);
  page.lineWidth(0.55);
  page.rect(tableX, tableY, tableWidth, tableHeight, 'B');
  page.fillColor([0.96, 0.96, 0.96]);
  page.rect(tableX, tableY, tableWidth, headerHeight, 'f');
  page.strokeColor([0.48, 0.48, 0.48]);
  page.line(tableX, tableY + headerHeight, tableX + tableWidth, tableY + headerHeight);

  let cx = tableX;
  ['ID', 'Typ', 'X', 'Y', '\u0160/\u00d8', 'D\u013a\u017eka', 'R', 'Uhol'].forEach((label, col) => {
    if (col > 0) {
      page.strokeColor([0.72, 0.72, 0.72]);
      page.line(cx, tableY, cx, tableY + tableHeight);
    }
    page.text(cx + colWidths[col] / 2, tableY + 13.2, label, {
      size: 6.8,
      align: 'center',
    });
    cx += colWidths[col];
  });

  if (rows.length === 0) {
    page.text(tableX + tableWidth / 2, tableY + headerHeight + 16, 'Bez otvorov a rezov', {
      size: 8,
      align: 'center',
      color: [0.36, 0.36, 0.36],
    });
    return;
  }

  rows.forEach((row, index) => {
    const y = tableY + headerHeight + index * rowHeight;
    if (index > 0) {
      page.strokeColor([0.9, 0.9, 0.9]);
      page.line(tableX, y, tableX + tableWidth, y);
    }

    let cellX = tableX;
    const values = [row.id, row.type, row.x, row.y, row.primary, row.length, row.radius, row.angle];

    values.forEach((value, col) => {
      if (col === 0) {
        page.strokeColor(blue);
        page.fillColor([1, 1, 1]);
        page.lineWidth(0.42);
        page.circle(cellX + colWidths[col] / 2, y + rowHeight / 2, markerRadius, 'B');
        page.text(cellX + colWidths[col] / 2, y + rowHeight / 2 + bodyFontSize * 0.34, value, {
          size: Math.max(4.5, bodyFontSize - 1.4),
          align: 'center',
          color: blue,
        });
      } else {
        page.text(cellX + colWidths[col] / 2, y + rowHeight * 0.64, value, {
          size: col === 1 ? Math.max(5.4, bodyFontSize - 0.4) : bodyFontSize,
          align: 'center',
        });
      }
      cellX += colWidths[col];
    });
  });

  page.text(tableX, tableY + tableHeight + 9, 'X/Y v mm od z\u00e1kladn\u00ed B/A.', {
    size: 5.8,
    color: [0.32, 0.32, 0.32],
  });
}

function drawBendLineTable(
  page: PdfPage,
  config: ConfiguratorState,
  box: { x: number; y: number; width: number; height: number },
) {
  const rows = getProductBendLines(config);
  const headerHeight = 18;
  const rowHeight = Math.max(
    10,
    Math.min(17, (box.height - headerHeight - 4) / Math.max(rows.length, 1)),
  );
  const blue: Rgb = [0.08, 0.16, 0.78];
  const baseColWidths = [32, 72, 72, 48, 72];
  const tableWidth = Math.min(
    box.width,
    baseColWidths.reduce((sum, width) => sum + width, 0),
  );
  const scale = tableWidth / baseColWidths.reduce((sum, width) => sum + width, 0);
  const colWidths = baseColWidths.map(width => width * scale);
  const tableX = box.x + box.width / 2 - tableWidth / 2;
  const tableY = box.y + 8;
  const tableHeight = headerHeight + Math.max(rows.length, 1) * rowHeight;

  page.text(box.x, box.y - 13, 'Ohybov\u00e9 l\u00ednie', { size: 10.2 });
  page.strokeColor([0.48, 0.48, 0.48]);
  page.fillColor([1, 1, 1]);
  page.lineWidth(0.48);
  page.rect(tableX, tableY, tableWidth, tableHeight, 'B');
  page.fillColor([0.96, 0.96, 0.96]);
  page.rect(tableX, tableY, tableWidth, headerHeight, 'f');
  page.strokeColor([0.48, 0.48, 0.48]);
  page.line(tableX, tableY + headerHeight, tableX + tableWidth, tableY + headerHeight);

  let cx = tableX;
  ['Poz.', 'L\u00ednia', 'Poloha [mm]', 'Uhol', 'Smer'].forEach((label, col) => {
    if (col > 0) {
      page.strokeColor([0.72, 0.72, 0.72]);
      page.line(cx, tableY, cx, tableY + tableHeight);
    }
    page.text(cx + colWidths[col] / 2, tableY + 12.2, label, {
      size: 6.5,
      align: 'center',
    });
    cx += colWidths[col];
  });

  if (rows.length === 0) {
    page.text(tableX + tableWidth / 2, tableY + headerHeight + 11, 'Bez ohybov', {
      size: 7,
      align: 'center',
      color: [0.36, 0.36, 0.36],
    });
    return;
  }

  rows.forEach((row, index) => {
    const y = tableY + headerHeight + index * rowHeight;
    if (index > 0) {
      page.strokeColor([0.9, 0.9, 0.9]);
      page.line(tableX, y, tableX + tableWidth, y);
    }
    const position =
      row.orientation === 'horizontal'
        ? `${compactNumber(config.lengthMm - row.positionMm)} od A`
        : `${compactNumber(row.positionMm)} od B`;
    const values = [
      `B${index + 1}`,
      row.orientation === 'horizontal' ? 'vodorovn\u00e1' : 'zvisl\u00e1',
      position,
      `${compactNumber(row.angleDeg)}${DEGREE_SYMBOL}`,
      row.direction === 'down' ? 'nadol' : 'nahor',
    ];
    let cellX = tableX;
    values.forEach((value, col) => {
      if (col === 0) {
        page.strokeColor(blue);
        page.fillColor([1, 1, 1]);
        page.circle(cellX + colWidths[col] / 2, y + rowHeight / 2, 4.4, 'B');
        page.text(cellX + colWidths[col] / 2, y + rowHeight / 2 + 2.3, value, {
          size: 5.4,
          align: 'center',
          color: blue,
        });
      } else {
        page.text(cellX + colWidths[col] / 2, y + rowHeight * 0.65, value, {
          size: 6,
          align: 'center',
        });
      }
      cellX += colWidths[col];
    });
  });
}

function drawToleranceBlock(
  page: PdfPage,
  config: ConfiguratorState,
  box: { x: number; y: number; width: number; height: number },
) {
  const selected = getSelectedOptions(config);
  const process = selected.machine.process === 'plasma' ? 'Plazma' : 'Laser';
  const bendLines = getProductBendLines(config);
  const rows = [
    ['Rozmery', 'v mm, ak nie je uveden\u00e9 inak'],
    ['Tolerancie', 'ISO 2768-mK'],
    ['Poloha prvkov', PLUS_MINUS_SYMBOL + '0,2 mm'],
    ['Hr\u00fabka', `${compactNumber(config.thicknessMm)} ${PLUS_MINUS_SYMBOL}0,3 mm`],
    ['Rezanie', process],
    ...(bendLines.length
      ? [['Ohyby', `${bendLines.length} definované ohybové línie podľa hlavného pohľadu`]]
      : []),
    ['Hrany', 'Odihli\u0165 a odstr\u00e1ni\u0165 otrepy'],
  ];
  const normalizedRows = rows.map(row =>
    row[0] === 'Ohyby'
      ? ['Ohyby', `${bendLines.length} l\u00ednie pod\u013ea hlavn\u00e9ho poh\u013eadu`]
      : row,
  );
  const rowHeight = Math.max(
    11.2,
    Math.min(16.2, (box.height - 18) / Math.max(normalizedRows.length, 1)),
  );
  const notesY = box.y + normalizedRows.length * rowHeight;

  page.text(box.x, box.y - 12, 'Tolerancie a pozn\u00e1mky', { size: 10.8 });
  page.strokeColor([0.28, 0.28, 0.28]);
  page.fillColor([1, 1, 1]);
  page.lineWidth(0.55);
  page.rect(box.x, box.y, box.width, box.height, 'B');
  page.line(box.x + 112, box.y, box.x + 112, notesY);

  normalizedRows.forEach((row, index) => {
    const y = box.y + index * rowHeight;
    if (index > 0) {
      page.strokeColor([0.76, 0.76, 0.76]);
      page.line(box.x, y, box.x + box.width, y);
    }
    page.text(box.x + 6, y + rowHeight * 0.72, row[0], { size: 6.8, color: [0.34, 0.34, 0.34] });
    page.text(box.x + 120, y + rowHeight * 0.72, row[1], { size: 7.1 });
  });

  page.strokeColor([0.76, 0.76, 0.76]);
  page.line(box.x, notesY, box.x + box.width, notesY);

  page.text(
    box.x + 6,
    notesY + 12,
    'Referen\u010dn\u00e9 z\u00e1kladne: A = spodn\u00e1 hrana, B = \u013eav\u00e1 hrana.',
    { size: 6.8, color: [0.2, 0.2, 0.2] },
  );
}

function drawTechnicalPdf(
  config: ConfiguratorState,
  estimate: ConfiguratorEstimate,
  isoImage?: PdfJpegImage,
  bentImages?: BentPreviewImage[],
) {
  const selected = getSelectedOptions(config);
  const pdf = new PdfDocument();
  const page = pdf.addPage(A3_LANDSCAPE.width, A3_LANDSCAPE.height);
  drawFrame(page, 'Lisonex / konfigurátor');

  page.text(58, 62, 'V\u00fdrobn\u00fd v\u00fdkres', { size: 18 });
  page.text(58, 85, selected.product.name, { size: 11 });
  page.text(page.width - 58, 64, QUOTE_ID, {
    size: 9.4,
    align: 'right',
    color: [0.32, 0.32, 0.32],
  });
  page.text(page.width - 58, 82, 'D\u00e1tum: ' + todayLabel(), {
    size: 8,
    align: 'right',
    color: [0.36, 0.36, 0.36],
  });
  page.strokeColor([0.1, 0.1, 0.1]);
  page.lineWidth(0.55);
  page.line(58, 100, page.width - 58, 100);

  drawFrontView(page, config, { x: 74, y: 132, width: 700, height: 380 }, true);
  drawFeatureCoordinateTable(page, config, { x: 806, y: 134, width: 326, height: 300 });
  drawBendLineTable(page, config, { x: 806, y: 472, width: 326, height: 72 });
  drawBentPreview(page, pdf, config, bentImages, { x: 806, y: 560, width: 326, height: 70 });

  page.text(58, 590, 'Izometrick\u00fd poh\u013ead', { size: 10.6 });
  if (isoImage) {
    const isoImageName = pdf.addJpegImage(isoImage);
    drawJpegImageContain(page, isoImageName, isoImage, { x: 54, y: 602, width: 286, height: 190 });
  } else {
    drawIsoView(page, config, { x: 54, y: 606, width: 270, height: 174 });
  }
  drawToleranceBlock(page, config, { x: 352, y: 612, width: 260, height: 124 });

  drawTitleBlock(page, config, estimate);
  return pdf.bytes();
}

const NEX_FILE_HEADER_TEMPLATE =
  'KioqKgBOT05BTUUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/////////wAAAAD//////////wAAAAAAAAYAAAAAAMB6IEEAAACQZCIgQZDrD+XdFsZA7fvPLfl6wkAAAAAAAMiUQAAAAAAAQI9AAAAAAAAAEEAAAAAAAAAgQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAcEEAAAAAAAAAAJoWAAAqAAAAAAAAAAYAAAAAAAAAAAAAAAAAAAAA';
const NEX_PART_HEADER_TEMPLATE =
  'UExBVNJBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP////////+w5itqAAAAAAwGAAAAAAAAAAAAAAD59UAAAADAMIP1QMDkv4bSc51AAAAAAAAAEECS+r/n9qOYQAAAAAAAAAAAAAAAAAAAAAAAAAAAAMByQAAAAAAAwHJAAAAAAAAAAAABAAAAAQAAAKABAAAfBQAABgAAAOoHBwAgB7iEAAAAAAAAAAAAAAAAAAAAAAAAAAA=';
const NEX_ENTITY_STEP = 64;
const NEX_BASE_SHEET_WIDTH_MM = 1500;
const NEX_BASE_SHEET_LENGTH_MM = 3000;
const NEX_DEFAULT_PART_GAP_MM = 5;
const NEX_PART_MARGIN_MM = 10;
function writeBytes(bytes: Uint8Array, offset: number, values: number[]) {
  bytes.set(values, offset);
}

function decodeBase64Bytes(value: string) {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function writeUint32(view: DataView, offset: number, value: number) {
  view.setUint32(offset, value >>> 0, true);
}

function writeFloat64(view: DataView, offset: number, value: number) {
  view.setFloat64(offset, value, true);
}

type NexPoint = { xMm: number; yMm: number };
type NexCircularCut = { kind: 'circle'; xMm: number; yMm: number; diameterMm: number };
type NexPolylineCut = { kind: 'polyline'; path: GeometryPath };
type NexCut = NexCircularCut | NexPolylineCut;
type NexPart = {
  originXmm: number;
  originYmm: number;
  widthMm: number;
  lengthMm: number;
  rotated: boolean;
  edgeRadiusMm: number;
  cuts: NexCut[];
  outlinePath: GeometryPath;
};
type NexEntity =
  | { kind: 'cut'; part: NexPart; cut: NexCut; byteLength: number }
  | { kind: 'contour'; part: NexPart; cut: NexPolylineCut; byteLength: number };

function getNexSheetSize(config: ConfiguratorState) {
  return {
    widthMm: Math.max(
      config.sheetWidthMm || NEX_BASE_SHEET_WIDTH_MM,
      config.widthMm + NEX_PART_MARGIN_MM * 2,
    ),
    lengthMm: Math.max(
      config.sheetLengthMm || NEX_BASE_SHEET_LENGTH_MM,
      config.lengthMm + NEX_PART_MARGIN_MM * 2,
    ),
  };
}

function getNexUsableSheetArea(config: ConfiguratorState) {
  const sheetSize = getNexSheetSize(config);

  return {
    xMm: NEX_PART_MARGIN_MM,
    yMm: NEX_PART_MARGIN_MM,
    widthMm: Math.max(0, sheetSize.widthMm - NEX_PART_MARGIN_MM * 2),
    lengthMm: Math.max(0, sheetSize.lengthMm - NEX_PART_MARGIN_MM * 2),
  };
}

function getNexCornerRadiusMm(part: Pick<NexPart, 'widthMm' | 'lengthMm' | 'edgeRadiusMm'>) {
  return Math.max(0, Math.min(part.edgeRadiusMm, part.widthMm / 2, part.lengthMm / 2));
}

function writeNexRelativeMove(
  view: DataView,
  offset: number,
  currentPosition: NexPoint,
  targetPosition: NexPoint,
) {
  writeFloat64(view, offset, targetPosition.xMm - currentPosition.xMm);
  writeFloat64(view, offset + 8, targetPosition.yMm - currentPosition.yMm);
  return targetPosition;
}

function transformNexPoint(point: NexPoint, part: NexPart, config: ConfiguratorState): NexPoint {
  if (part.rotated) {
    return {
      xMm: part.originXmm + point.yMm,
      yMm: part.originYmm + (config.widthMm - point.xMm),
    };
  }

  return {
    xMm: part.originXmm + point.xMm,
    yMm: part.originYmm + point.yMm,
  };
}

function getNexCircleStart(part: NexPart, cut: NexCircularCut, config: ConfiguratorState) {
  const center = transformNexPoint({ xMm: cut.xMm, yMm: cut.yMm }, part, config);

  return {
    xMm: center.xMm - cut.diameterMm / 2,
    yMm: center.yMm,
  };
}

function normalizeNexPathPoints(points: NexPoint[]) {
  const normalized: NexPoint[] = [];

  points.forEach(point => {
    const previous = normalized[normalized.length - 1];
    if (
      previous &&
      Math.abs(previous.xMm - point.xMm) < 0.001 &&
      Math.abs(previous.yMm - point.yMm) < 0.001
    ) {
      return;
    }

    normalized.push(point);
  });

  const first = normalized[0];
  const last = normalized[normalized.length - 1];
  if (
    first &&
    last &&
    normalized.length > 1 &&
    Math.abs(first.xMm - last.xMm) < 0.001 &&
    Math.abs(first.yMm - last.yMm) < 0.001
  ) {
    normalized.pop();
  }

  return normalized;
}

function getNexTransformedPath(cut: NexPolylineCut, part: NexPart, config: ConfiguratorState) {
  return normalizeNexPathPoints(
    cut.path.points.map(point => transformNexPoint(point, part, config)),
  );
}

function getNexPathSegments(points: NexPoint[], closed: boolean) {
  if (points.length < 2) return [];

  const segments: NexPoint[] = [];
  const limit = closed ? points.length : points.length - 1;

  for (let index = 0; index < limit; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    const dxMm = next.xMm - current.xMm;
    const dyMm = next.yMm - current.yMm;

    if (Math.hypot(dxMm, dyMm) < 0.001) continue;
    segments.push({ xMm: dxMm, yMm: dyMm });
  }

  return segments;
}

function getNexPolylineByteLength(cut: NexPolylineCut) {
  const pointCount = normalizeNexPathPoints(cut.path.points).length;
  const segmentCount = cut.path.closed ? pointCount : Math.max(0, pointCount - 1);

  if (segmentCount <= 0) return 0;

  return 28 + 16 + Math.max(0, segmentCount - 1) * 18 + 4;
}

function getNexCutByteLength(cut: NexCut) {
  return cut.kind === 'circle' ? NEX_ENTITY_STEP : getNexPolylineByteLength(cut);
}

function writeNexCircleCut(
  bytes: Uint8Array,
  view: DataView,
  offset: number,
  part: NexPart,
  cut: NexCircularCut,
  currentPosition: NexPoint,
  config: ConfiguratorState,
) {
  const circleStart = getNexCircleStart(part, cut, config);

  writeUint32(view, offset, 5);
  const nextPosition = writeNexRelativeMove(view, offset + 4, currentPosition, circleStart);
  writeBytes(bytes, offset + 20, [0x06, 0x00, 0x1e, 0x00, 0x07, 0x00, 0xff, 0xff]);
  writeFloat64(view, offset + 44, cut.diameterMm / 2);
  writeBytes(bytes, offset + 60, [0x08, 0x00, 0x26, 0x00]);

  return nextPosition;
}

function writeNexPolylineCut(
  bytes: Uint8Array,
  view: DataView,
  offset: number,
  part: NexPart,
  cut: NexPolylineCut,
  currentPosition: NexPoint,
  config: ConfiguratorState,
) {
  const points = getNexTransformedPath(cut, part, config);
  const start = points[0] ?? currentPosition;
  const segments = getNexPathSegments(points, cut.path.closed);

  writeUint32(view, offset, 5);
  writeNexRelativeMove(view, offset + 4, currentPosition, start);
  writeBytes(bytes, offset + 20, [0x06, 0x00, 0x1e, 0x00, 0x07, 0x00, 0x00, 0x00]);

  let cursor = offset + 28;
  segments.forEach((segment, index) => {
    if (index > 0) {
      writeUint16(view, cursor, 0);
      cursor += 2;
    }

    writeFloat64(view, cursor, segment.xMm);
    writeFloat64(view, cursor + 8, segment.yMm);
    cursor += 16;
  });

  writeBytes(bytes, cursor, [0x08, 0x00, 0x26, 0x00]);

  return start;
}

function writeNexCut(
  bytes: Uint8Array,
  view: DataView,
  offset: number,
  part: NexPart,
  cut: NexCut,
  currentPosition: NexPoint,
  config: ConfiguratorState,
) {
  if (cut.kind === 'circle') {
    return writeNexCircleCut(bytes, view, offset, part, cut, currentPosition, config);
  }

  return writeNexPolylineCut(bytes, view, offset, part, cut, currentPosition, config);
}

export function getNexUnsupportedFeatureLabels(config: ConfiguratorState) {
  const geometry = resolvePartGeometry(config);

  return geometry.exportEntities
    .filter(entity => {
      if (entity.kind === 'outline' || entity.kind === 'bendLine') return false;
      if (!('feature' in entity) || !entity.feature.enabled) return false;
      if (entity.feature.kind === 'edgeCut') return false;
      return !entity.path;
    })
    .map(entity => ('feature' in entity ? entity.feature.label : entity.id));
}

export function canExportNex(config: ConfiguratorState) {
  return getNexUnsupportedFeatureLabels(config).length === 0;
}

function createNexPartCuts(config: ConfiguratorState) {
  return resolvePartGeometry(config).exportEntities.flatMap<NexCut>(entity => {
    if (entity.kind === 'outline' || entity.kind === 'bendLine') return [];
    if (!('feature' in entity) || !entity.feature.enabled) return [];
    const feature = entity.feature;
    const center = feature.position;

    if (feature.kind === 'edgeCut') return [];

    if (feature.kind === 'roundOpening' && center) {
      return [
        {
          kind: 'circle',
          xMm: center.xMm,
          yMm: center.yMm,
          diameterMm: feature.diameterMm,
        },
      ];
    }

    if (feature.kind === 'internalCut' && feature.shape === 'round' && center) {
      return [
        {
          kind: 'circle',
          xMm: center.xMm,
          yMm: center.yMm,
          diameterMm: feature.widthMm,
        },
      ];
    }

    if (entity.path) return [{ kind: 'polyline', path: entity.path }];

    return [];
  });
}

function createNexPartOutline(config: ConfiguratorState): NexPolylineCut {
  return {
    kind: 'polyline',
    path: resolvePartGeometry(config).finalOutlinePath,
  };
}

function getNexPartGapMm(config: ConfiguratorState) {
  return Math.max(0, Math.min(config.nestingGapMm ?? NEX_DEFAULT_PART_GAP_MM, 100));
}

type NexPackingPlacement = {
  originXmm: number;
  originYmm: number;
  widthMm: number;
  lengthMm: number;
  rotated: boolean;
};

type NexPackingCandidate = {
  placements: NexPackingPlacement[];
  usedAreaMm2: number;
};

function getNexPartSize(config: ConfiguratorState, rotated: boolean) {
  return {
    widthMm: rotated ? config.lengthMm : config.widthMm,
    lengthMm: rotated ? config.widthMm : config.lengthMm,
  };
}

function getNexGridLayout(
  area: { xMm: number; yMm: number; widthMm: number; lengthMm: number },
  partSize: { widthMm: number; lengthMm: number },
  gapMm: number,
  rotated: boolean,
) {
  const columns = Math.floor((area.widthMm + gapMm + 0.001) / (partSize.widthMm + gapMm));
  const rows = Math.floor((area.lengthMm + gapMm + 0.001) / (partSize.lengthMm + gapMm));
  const placements: NexPackingPlacement[] = [];

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      placements.push({
        originXmm: area.xMm + column * (partSize.widthMm + gapMm),
        originYmm: area.yMm + row * (partSize.lengthMm + gapMm),
        widthMm: partSize.widthMm,
        lengthMm: partSize.lengthMm,
        rotated,
      });
    }
  }

  return {
    placements,
    columns,
    rows,
    usedWidthMm: columns > 0 ? columns * partSize.widthMm + (columns - 1) * gapMm : 0,
    usedLengthMm: rows > 0 ? rows * partSize.lengthMm + (rows - 1) * gapMm : 0,
  };
}

function getSingleOrientationCandidate(
  config: ConfiguratorState,
  rotated: boolean,
): NexPackingCandidate {
  const gapMm = getNexPartGapMm(config);
  const partSize = getNexPartSize(config, rotated);
  const grid = getNexGridLayout(getNexUsableSheetArea(config), partSize, gapMm, rotated);

  return {
    placements: grid.placements,
    usedAreaMm2: grid.placements.length * config.widthMm * config.lengthMm,
  };
}

function getMixedOrientationCandidate(config: ConfiguratorState, primaryRotated: boolean) {
  const usableArea = getNexUsableSheetArea(config);
  const gapMm = getNexPartGapMm(config);
  const primarySize = getNexPartSize(config, primaryRotated);
  const secondarySize = getNexPartSize(config, !primaryRotated);
  const mainGrid = getNexGridLayout(usableArea, primarySize, gapMm, primaryRotated);
  const placements = [...mainGrid.placements];

  if (mainGrid.placements.length > 0) {
    const rightStripX = usableArea.xMm + mainGrid.usedWidthMm + gapMm;
    const rightStripWidth = usableArea.xMm + usableArea.widthMm - rightStripX;
    if (rightStripWidth >= secondarySize.widthMm) {
      placements.push(
        ...getNexGridLayout(
          {
            xMm: rightStripX,
            yMm: usableArea.yMm,
            widthMm: rightStripWidth,
            lengthMm: usableArea.lengthMm,
          },
          secondarySize,
          gapMm,
          !primaryRotated,
        ).placements,
      );
    }

    const topStripY = usableArea.yMm + mainGrid.usedLengthMm + gapMm;
    const topStripLength = usableArea.yMm + usableArea.lengthMm - topStripY;
    if (mainGrid.usedWidthMm >= secondarySize.widthMm && topStripLength >= secondarySize.lengthMm) {
      placements.push(
        ...getNexGridLayout(
          {
            xMm: usableArea.xMm,
            yMm: topStripY,
            widthMm: mainGrid.usedWidthMm,
            lengthMm: topStripLength,
          },
          secondarySize,
          gapMm,
          !primaryRotated,
        ).placements,
      );
    }
  }

  return {
    placements,
    usedAreaMm2: placements.length * config.widthMm * config.lengthMm,
  };
}

function getBestNexPackingCandidate(config: ConfiguratorState) {
  const candidates = [
    getSingleOrientationCandidate(config, false),
    getSingleOrientationCandidate(config, true),
    getMixedOrientationCandidate(config, false),
    getMixedOrientationCandidate(config, true),
  ];

  return candidates.reduce((best, candidate) => {
    if (candidate.placements.length !== best.placements.length) {
      return candidate.placements.length > best.placements.length ? candidate : best;
    }

    if (candidate.usedAreaMm2 !== best.usedAreaMm2) {
      return candidate.usedAreaMm2 > best.usedAreaMm2 ? candidate : best;
    }

    return best;
  });
}

function buildNexSheets(config: ConfiguratorState) {
  const quantity = Math.max(1, Math.floor(config.quantity || 1));
  const candidate = getBestNexPackingCandidate(config);
  const cuts = createNexPartCuts(config);
  const outlinePath = createNexPartOutline(config);
  const sheets: NexPart[][] = [];
  let remaining = quantity;

  while (remaining > 0) {
    const count = Math.min(candidate.placements.length, remaining);
    const parts = candidate.placements.slice(0, count).map(placement => ({
      originXmm: placement.originXmm,
      originYmm: placement.originYmm,
      widthMm: placement.widthMm,
      lengthMm: placement.lengthMm,
      rotated: placement.rotated,
      edgeRadiusMm: config.edgeRadiusMm,
      cuts,
      outlinePath: outlinePath.path,
    }));

    sheets.push(parts);
    remaining -= count;
  }

  return sheets;
}

function getNexPartEntities(part: NexPart) {
  const entities: NexEntity[] = [];

  part.cuts.forEach(cut => {
    const byteLength = getNexCutByteLength(cut);
    if (byteLength <= 0) return;
    entities.push({ kind: 'cut', part, cut, byteLength });
  });

  const contour: NexPolylineCut = { kind: 'polyline', path: part.outlinePath };
  const contourLength = getNexPolylineByteLength(contour);
  if (contourLength > 0) {
    entities.push({ kind: 'contour', part, cut: contour, byteLength: contourLength });
  }

  return entities;
}

function getNexEntities(parts: NexPart[]) {
  return parts.flatMap(part => getNexPartEntities(part));
}

function getNexPartEndPointer(partEndOffset: number) {
  return partEndOffset & 0xffff;
}

function getNexPartInternalId(partEndOffset: number) {
  return (0x01 << 24) | ((getNexPartEndPointer(partEndOffset) >> 8) & 0xff);
}

function getNexPartChainValue(partEndOffset: number, previousPartEndOffset?: number) {
  const previousHandle =
    previousPartEndOffset === undefined ? 1 : getNexPartInternalId(previousPartEndOffset) & 0xff;

  return ((getNexPartEndPointer(partEndOffset) & 0xff) << 24) | previousHandle;
}

function writeNexPartHeader(
  bytes: Uint8Array,
  view: DataView,
  offset: number,
  partTemplate: Uint8Array,
  part: NexPart,
  partEndOffset: number,
  previousPartEndOffset?: number,
) {
  bytes.set(partTemplate, offset);
  const chainValue = getNexPartChainValue(partEndOffset, previousPartEndOffset);
  writeUint32(
    view,
    offset + 0x16c,
    previousPartEndOffset === undefined
      ? 0xa0000000
      : (getNexPartEndPointer(previousPartEndOffset) & 0xff) << 24,
  );
  writeUint32(view, offset + 0x170, chainValue);
  writeUint32(view, offset + 0x174, getNexPartInternalId(partEndOffset));

  const entityCount = getNexPartEntities(part).length;
  bytes[offset + 0x17c] = 0x00;
  bytes[offset + 0x17d] = entityCount & 0xff;
  bytes[offset + 0x180] = 0x07;
}

function drawNexFile(config: ConfiguratorState, parts: NexPart[]) {
  const fileHeaderTemplate = decodeBase64Bytes(NEX_FILE_HEADER_TEMPLATE);
  const partHeaderTemplate = decodeBase64Bytes(NEX_PART_HEADER_TEMPLATE);
  const entityCount = getNexEntities(parts).length;
  const interPartPaddingBytes = Math.max(0, parts.length - 1);
  const totalLength =
    fileHeaderTemplate.length +
    interPartPaddingBytes +
    parts.reduce(
      (sum, part) =>
        sum +
        partHeaderTemplate.length +
        getNexPartEntities(part).reduce((entitySum, entity) => entitySum + entity.byteLength, 0),
      0,
    );
  const bytes = new Uint8Array(totalLength);
  bytes.set(fileHeaderTemplate, 0);
  const view = new DataView(bytes.buffer);

  const sheetSize = getNexSheetSize(config);

  writeUint32(view, 0x11e, parts.length);
  writeFloat64(view, 0x140, sheetSize.widthMm);
  writeFloat64(view, 0x148, sheetSize.lengthMm);
  writeUint32(view, 0x184, totalLength);
  writeUint32(view, 0x188, entityCount);
  writeUint32(view, 0x190, parts.length);

  let offset = fileHeaderTemplate.length;

  let previousPartEndOffset: number | undefined;

  parts.forEach((part, partIndex) => {
    let currentPosition: NexPoint = { xMm: 0, yMm: 0 };
    const partEntities = getNexPartEntities(part);
    const partEntityLength = partEntities.reduce((sum, entity) => sum + entity.byteLength, 0);
    const partEndOffset = offset + partHeaderTemplate.length + partEntityLength;

    writeNexPartHeader(
      bytes,
      view,
      offset,
      partHeaderTemplate,
      part,
      partEndOffset,
      previousPartEndOffset,
    );
    offset += partHeaderTemplate.length;

    partEntities.forEach(entity => {
      currentPosition = writeNexCut(
        bytes,
        view,
        offset,
        entity.part,
        entity.cut,
        currentPosition,
        config,
      );

      offset += entity.byteLength;
    });

    previousPartEndOffset = partEndOffset;

    if (partIndex < parts.length - 1) {
      offset += 1;
    }
  });

  if (offset !== totalLength) {
    throw new Error(
      `Invalid NEX export size: expected ${totalLength} bytes, wrote ${offset} bytes.`,
    );
  }

  return bytes;
}

export type NexSheetPreview = {
  sheetWidthMm: number;
  sheetLengthMm: number;
  marginMm: number;
  parts: Array<{ xMm: number; yMm: number; widthMm: number; lengthMm: number }>;
};

export function getNexSheetPreview(config: ConfiguratorState): NexSheetPreview[] {
  const sheetSize = getNexSheetSize(config);

  return buildNexSheets(config).map(parts => ({
    sheetWidthMm: sheetSize.widthMm,
    sheetLengthMm: sheetSize.lengthMm,
    marginMm: NEX_PART_MARGIN_MM,
    parts: parts.map(part => ({
      xMm: part.originXmm,
      yMm: part.originYmm,
      widthMm: part.widthMm,
      lengthMm: part.lengthMm,
    })),
  }));
}

function drawNexFiles(config: ConfiguratorState) {
  const unsupportedFeatures = getNexUnsupportedFeatureLabels(config);
  if (unsupportedFeatures.length > 0) {
    throw new Error(`NEX export zatiaľ nepodporuje: ${unsupportedFeatures.join(', ')}`);
  }

  return buildNexSheets(config).map(parts => drawNexFile(config, parts));
}

function sanitizeExportBaseName(value: string) {
  return value
    .trim()
    .replace(/\.(?:dxf|nex3?|pdf|zip)$/i, '')
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[.-]+|[.-]+$/g, '')
    .slice(0, 80);
}

export function getNexExportBaseName(config: ConfiguratorState) {
  const customName = sanitizeExportBaseName(
    config.configurationName?.trim() || getSelectedOptions(config).product.name,
  );
  if (customName) return customName;

  const edgeRadiusMm = Math.round(
    getNexCornerRadiusMm({
      widthMm: config.widthMm,
      lengthMm: config.lengthMm,
      edgeRadiusMm: config.edgeRadiusMm,
    }),
  );
  const cutCount = createNexPartCuts(config).length;

  return [
    'lisonex',
    Math.round(config.widthMm) + 'x' + Math.round(config.lengthMm),
    't' + Math.round(config.thicknessMm) + 'mm',
    'r' + edgeRadiusMm + 'mm',
    'c' + cutCount,
    'gap' + Math.round(getNexPartGapMm(config)) + 'mm',
    config.quantity + 'ks',
  ].join('-');
}

function getNexExportFileName(config: ConfiguratorState, index: number, total: number) {
  const sheetNumber = String(index + 1).padStart(2, '0');
  const suffix = total > 1 ? '-tabula-' + sheetNumber : '-tabula-01';

  return getNexExportBaseName(config) + suffix + '.NEX3';
}

export function getNexExportFiles(config: ConfiguratorState) {
  const files = drawNexFiles(config);

  return files.map((bytes, index) => ({
    bytes,
    filename: getNexExportFileName(config, index, files.length),
  }));
}

const DXF_LINE_BREAK = '\r\n';

function isDxfFloatingPointCode(code: number) {
  return (
    (code >= 10 && code <= 59) ||
    (code >= 110 && code <= 149) ||
    (code >= 210 && code <= 239) ||
    (code >= 460 && code <= 469) ||
    (code >= 1010 && code <= 1059)
  );
}

function formatDxfNumber(code: number, value: number) {
  if (!Number.isFinite(value)) {
    throw new Error(`Cannot write non-finite DXF value for group code ${code}.`);
  }

  if (!isDxfFloatingPointCode(code)) {
    if (!Number.isInteger(value)) {
      throw new Error(`DXF group code ${code} requires an integer value.`);
    }

    return value.toString();
  }

  const normalizedValue = Math.abs(value) < 0.0000005 ? 0 : value;
  return normalizedValue
    .toFixed(6)
    .replace(/(\.\d*?[1-9])0+$/, '$1')
    .replace(/\.0+$/, '.0');
}

function dxfPair(code: number, value: string | number) {
  const formattedValue = typeof value === 'number' ? formatDxfNumber(code, value) : value;
  return `${code}${DXF_LINE_BREAK}${formattedValue}${DXF_LINE_BREAK}`;
}

type DxfLineEntity = {
  kind: 'line';
  start: Point;
  end: Point;
};

type DxfCircleEntity = {
  kind: 'circle';
  center: Point;
  radius: number;
};

type DxfArcEntity = {
  kind: 'arc';
  center: Point;
  radius: number;
  startAngleDeg: number;
  endAngleDeg: number;
};

type DxfEntity = DxfLineEntity | DxfCircleEntity | DxfArcEntity;

function transformDxfPoint(point: Point, part: NexPart, config: ConfiguratorState): Point {
  if (part.rotated) {
    return {
      x: part.originXmm + point.y,
      y: part.originYmm + (config.widthMm - point.x),
    };
  }

  return {
    x: part.originXmm + point.x,
    y: part.originYmm + (config.lengthMm - point.y),
  };
}

function normalizeDxfAngle(angleDeg: number) {
  const normalized = angleDeg % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

function transformDxfAngleDeg(angleDeg: number, part: NexPart) {
  const angleRad = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(angleRad);
  const dy = Math.sin(angleRad);
  const vector = part.rotated ? { x: dy, y: -dx } : { x: dx, y: -dy };

  return normalizeDxfAngle((Math.atan2(vector.y, vector.x) * 180) / Math.PI);
}

function makeDxfArc(
  center: Point,
  radius: number,
  startAngleDeg: number,
  endAngleDeg: number,
  part: NexPart,
  config: ConfiguratorState,
): DxfArcEntity {
  const transformedStart = transformDxfAngleDeg(startAngleDeg, part);
  const transformedEnd = transformDxfAngleDeg(endAngleDeg, part);
  const ccwSweep = normalizeDxfAngle(transformedEnd - transformedStart);

  if (ccwSweep > 180) {
    return {
      kind: 'arc',
      center: transformDxfPoint(center, part, config),
      radius,
      startAngleDeg: transformedEnd,
      endAngleDeg: transformedStart,
    };
  }

  return {
    kind: 'arc',
    center: transformDxfPoint(center, part, config),
    radius,
    startAngleDeg: transformedStart,
    endAngleDeg: transformedEnd,
  };
}

function rotateLocalPoint(
  center: { xMm: number; yMm: number },
  xMm: number,
  yMm: number,
  angleDeg: number,
): Point {
  const angleRad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  return {
    x: center.xMm + xMm * cos - yMm * sin,
    y: center.yMm + xMm * sin + yMm * cos,
  };
}

function addDxfLine(
  entities: DxfEntity[],
  start: Point,
  end: Point,
  part: NexPart,
  config: ConfiguratorState,
) {
  if (Math.hypot(end.x - start.x, end.y - start.y) < 0.001) return;

  entities.push({
    kind: 'line',
    start: transformDxfPoint(start, part, config),
    end: transformDxfPoint(end, part, config),
  });
}

function addDxfRoundedRect(
  entities: DxfEntity[],
  center: { xMm: number; yMm: number },
  widthMm: number,
  heightMm: number,
  radiusMm: number,
  angleDeg: number,
  part: NexPart,
  config: ConfiguratorState,
) {
  const width = Math.max(widthMm, 0.1);
  const height = Math.max(heightMm, 0.1);
  const radius = Math.min(Math.max(radiusMm, 0), width / 2, height / 2);
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const localPoint = (xMm: number, yMm: number) => rotateLocalPoint(center, xMm, yMm, angleDeg);

  if (radius <= 0.001) {
    const points = [
      localPoint(-halfWidth, -halfHeight),
      localPoint(halfWidth, -halfHeight),
      localPoint(halfWidth, halfHeight),
      localPoint(-halfWidth, halfHeight),
    ];

    points.forEach((point, index) => {
      addDxfLine(entities, point, points[(index + 1) % points.length], part, config);
    });
    return;
  }

  addDxfLine(
    entities,
    localPoint(-halfWidth + radius, -halfHeight),
    localPoint(halfWidth - radius, -halfHeight),
    part,
    config,
  );
  entities.push(
    makeDxfArc(
      rotateLocalPoint(center, halfWidth - radius, -halfHeight + radius, angleDeg),
      radius,
      angleDeg - 90,
      angleDeg,
      part,
      config,
    ),
  );
  addDxfLine(
    entities,
    localPoint(halfWidth, -halfHeight + radius),
    localPoint(halfWidth, halfHeight - radius),
    part,
    config,
  );
  entities.push(
    makeDxfArc(
      rotateLocalPoint(center, halfWidth - radius, halfHeight - radius, angleDeg),
      radius,
      angleDeg,
      angleDeg + 90,
      part,
      config,
    ),
  );
  addDxfLine(
    entities,
    localPoint(halfWidth - radius, halfHeight),
    localPoint(-halfWidth + radius, halfHeight),
    part,
    config,
  );
  entities.push(
    makeDxfArc(
      rotateLocalPoint(center, -halfWidth + radius, halfHeight - radius, angleDeg),
      radius,
      angleDeg + 90,
      angleDeg + 180,
      part,
      config,
    ),
  );
  addDxfLine(
    entities,
    localPoint(-halfWidth, halfHeight - radius),
    localPoint(-halfWidth, -halfHeight + radius),
    part,
    config,
  );
  entities.push(
    makeDxfArc(
      rotateLocalPoint(center, -halfWidth + radius, -halfHeight + radius, angleDeg),
      radius,
      angleDeg + 180,
      angleDeg + 270,
      part,
      config,
    ),
  );
}

function addDxfPath(
  entities: DxfEntity[],
  path: ReturnType<typeof resolvePartGeometry>['finalOutlinePath'],
  part: NexPart,
  config: ConfiguratorState,
) {
  const points = path.points.map(point => ({ x: point.xMm, y: point.yMm }));
  const limit = path.closed ? points.length : points.length - 1;

  for (let index = 0; index < limit; index += 1) {
    const start = points[index];
    const end = points[(index + 1) % points.length];
    if (!start || !end) continue;
    addDxfLine(entities, start, end, part, config);
  }
}

function buildDxfEntities(config: ConfiguratorState, parts: NexPart[]) {
  const geometry = resolvePartGeometry(config);

  return parts.flatMap(part => {
    const entities: DxfEntity[] = [];
    addDxfPath(entities, geometry.finalOutlinePath, part, config);

    geometry.exportEntities.forEach(entity => {
      if (!('feature' in entity) || !entity.feature.enabled) return;
      const feature = entity.feature;
      const center = feature.position;
      if (!center) return;

      if (feature.kind === 'roundOpening') {
        entities.push({
          kind: 'circle',
          center: transformDxfPoint({ x: center.xMm, y: center.yMm }, part, config),
          radius: feature.diameterMm / 2,
        });
        return;
      }

      if (feature.kind === 'slotOpening') {
        addDxfRoundedRect(
          entities,
          center,
          feature.lengthMm,
          feature.widthMm,
          feature.widthMm / 2,
          feature.angleDeg,
          part,
          config,
        );
        return;
      }

      if (feature.kind === 'rectOpening') {
        addDxfRoundedRect(
          entities,
          center,
          feature.widthMm,
          feature.heightMm,
          feature.cornerRadiusMm ?? 0,
          feature.angleDeg,
          part,
          config,
        );
        return;
      }

      if (feature.kind === 'internalCut') {
        if (feature.shape === 'round') {
          entities.push({
            kind: 'circle',
            center: transformDxfPoint({ x: center.xMm, y: center.yMm }, part, config),
            radius: feature.widthMm / 2,
          });
          return;
        }

        addDxfRoundedRect(
          entities,
          center,
          feature.lengthMm,
          feature.widthMm,
          feature.cornerRadiusMm ?? (feature.shape === 'slot' ? feature.widthMm / 2 : 0),
          feature.angleDeg,
          part,
          config,
        );
      }
    });

    return entities;
  });
}

function getDxfEntityBounds(entity: DxfEntity) {
  if (entity.kind === 'line') {
    return {
      minX: Math.min(entity.start.x, entity.end.x),
      minY: Math.min(entity.start.y, entity.end.y),
      maxX: Math.max(entity.start.x, entity.end.x),
      maxY: Math.max(entity.start.y, entity.end.y),
    };
  }

  return {
    minX: entity.center.x - entity.radius,
    minY: entity.center.y - entity.radius,
    maxX: entity.center.x + entity.radius,
    maxY: entity.center.y + entity.radius,
  };
}

function drawDxfFile(config: ConfiguratorState, parts: NexPart[]) {
  const entities = buildDxfEntities(config, parts);
  const bounds = entities.map(getDxfEntityBounds);
  const minX = bounds.length ? Math.min(...bounds.map(bound => bound.minX)) : 0;
  const minY = bounds.length ? Math.min(...bounds.map(bound => bound.minY)) : 0;
  const maxX = bounds.length ? Math.max(...bounds.map(bound => bound.maxX)) : config.sheetWidthMm;
  const maxY = bounds.length ? Math.max(...bounds.map(bound => bound.maxY)) : config.sheetLengthMm;
  const chunks: string[] = [
    dxfPair(0, 'SECTION'),
    dxfPair(2, 'HEADER'),
    dxfPair(9, '$ACADVER'),
    dxfPair(1, 'AC1009'),
    dxfPair(9, '$EXTMIN'),
    dxfPair(10, minX),
    dxfPair(20, minY),
    dxfPair(30, 0),
    dxfPair(9, '$EXTMAX'),
    dxfPair(10, maxX),
    dxfPair(20, maxY),
    dxfPair(30, 0),
    dxfPair(0, 'ENDSEC'),
    dxfPair(0, 'SECTION'),
    dxfPair(2, 'ENTITIES'),
  ];

  entities.forEach(entity => {
    if (entity.kind === 'line') {
      chunks.push(
        dxfPair(0, 'LINE'),
        dxfPair(8, '0'),
        dxfPair(10, entity.start.x),
        dxfPair(20, entity.start.y),
        dxfPair(30, 0),
        dxfPair(11, entity.end.x),
        dxfPair(21, entity.end.y),
        dxfPair(31, 0),
      );
      return;
    }

    if (entity.kind === 'circle') {
      chunks.push(
        dxfPair(0, 'CIRCLE'),
        dxfPair(8, '0'),
        dxfPair(10, entity.center.x),
        dxfPair(20, entity.center.y),
        dxfPair(30, 0),
        dxfPair(40, entity.radius),
      );
      return;
    }

    chunks.push(
      dxfPair(0, 'ARC'),
      dxfPair(8, '0'),
      dxfPair(10, entity.center.x),
      dxfPair(20, entity.center.y),
      dxfPair(30, 0),
      dxfPair(40, entity.radius),
      dxfPair(50, entity.startAngleDeg),
      dxfPair(51, entity.endAngleDeg),
    );
  });

  chunks.push(dxfPair(0, 'ENDSEC'), dxfPair(0, 'EOF'));

  return new TextEncoder().encode(chunks.join(''));
}

function drawDxfFiles(config: ConfiguratorState) {
  return buildNexSheets(config).map(parts => drawDxfFile(config, parts));
}

function getDxfExportFileName(config: ConfiguratorState, index: number, total: number) {
  const sheetNumber = String(index + 1).padStart(2, '0');
  const suffix = total > 1 ? '-tabula-' + sheetNumber : '-tabula-01';

  return getNexExportBaseName(config) + suffix + '.DXF';
}

export function getDxfExportFiles(config: ConfiguratorState) {
  const files = drawDxfFiles(config);

  return files.map((bytes, index) => ({
    bytes,
    filename: getDxfExportFileName(config, index, files.length),
  }));
}

function writeUint16(view: DataView, offset: number, value: number) {
  view.setUint16(offset, value & 0xffff, true);
}

function getCrc32(bytes: Uint8Array) {
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    crc ^= bytes[index];
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
    }
  }

  return (crc ^ 0xffffffff) >>> 0;
}

export function createZipFile(files: Array<{ filename: string; bytes: Uint8Array }>) {
  const utf8Flag = 0x0800;
  const encoder = new TextEncoder();
  const encodedFiles = files.map(file => ({
    ...file,
    nameBytes: encoder.encode(file.filename),
    crc32: getCrc32(file.bytes),
  }));
  const localSize = encodedFiles.reduce(
    (sum, file) => sum + 30 + file.nameBytes.length + file.bytes.length,
    0,
  );
  const centralSize = encodedFiles.reduce((sum, file) => sum + 46 + file.nameBytes.length, 0);
  const output = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(output.buffer);
  let offset = 0;
  const centralRecords: Array<{ file: (typeof encodedFiles)[number]; localOffset: number }> = [];

  encodedFiles.forEach(file => {
    const localOffset = offset;
    centralRecords.push({ file, localOffset });
    writeUint32(view, offset, 0x04034b50);
    writeUint16(view, offset + 4, 20);
    writeUint16(view, offset + 6, utf8Flag);
    writeUint16(view, offset + 8, 0);
    writeUint16(view, offset + 10, 0);
    writeUint16(view, offset + 12, 0);
    writeUint32(view, offset + 14, file.crc32);
    writeUint32(view, offset + 18, file.bytes.length);
    writeUint32(view, offset + 22, file.bytes.length);
    writeUint16(view, offset + 26, file.nameBytes.length);
    writeUint16(view, offset + 28, 0);
    offset += 30;
    output.set(file.nameBytes, offset);
    offset += file.nameBytes.length;
    output.set(file.bytes, offset);
    offset += file.bytes.length;
  });

  const centralOffset = offset;
  centralRecords.forEach(({ file, localOffset }) => {
    writeUint32(view, offset, 0x02014b50);
    writeUint16(view, offset + 4, 20);
    writeUint16(view, offset + 6, 20);
    writeUint16(view, offset + 8, utf8Flag);
    writeUint16(view, offset + 10, 0);
    writeUint16(view, offset + 12, 0);
    writeUint16(view, offset + 14, 0);
    writeUint32(view, offset + 16, file.crc32);
    writeUint32(view, offset + 20, file.bytes.length);
    writeUint32(view, offset + 24, file.bytes.length);
    writeUint16(view, offset + 28, file.nameBytes.length);
    writeUint16(view, offset + 30, 0);
    writeUint16(view, offset + 32, 0);
    writeUint16(view, offset + 34, 0);
    writeUint16(view, offset + 36, 0);
    writeUint32(view, offset + 38, 0);
    writeUint32(view, offset + 42, localOffset);
    offset += 46;
    output.set(file.nameBytes, offset);
    offset += file.nameBytes.length;
  });

  writeUint32(view, offset, 0x06054b50);
  writeUint16(view, offset + 4, 0);
  writeUint16(view, offset + 6, 0);
  writeUint16(view, offset + 8, files.length);
  writeUint16(view, offset + 10, files.length);
  writeUint32(view, offset + 12, centralSize);
  writeUint32(view, offset + 16, centralOffset);
  writeUint16(view, offset + 20, 0);

  return output;
}

function downloadBytes(bytes: Uint8Array, filename: string, type = 'application/octet-stream') {
  const data = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
function downloadPdf(bytes: Uint8Array, filename: string) {
  const data = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
  const blob = new Blob([data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function openTechnicalDrawingDocument(
  config: ConfiguratorState,
  estimate: ConfiguratorEstimate,
) {
  const document = await buildTechnicalDrawingDocument(config, estimate);
  downloadPdf(document.bytes, document.filename);
}

export function openNexSheetDocument(config: ConfiguratorState, index: number) {
  const file = buildNexSheetDocument(config, index);
  if (!file) return;

  downloadBytes(file.bytes, file.filename);
}

export function openNexDocument(config: ConfiguratorState) {
  const document = buildNexDocument(config);

  downloadBytes(document.bytes, document.filename, document.contentType);
}

export function openDxfSheetDocument(config: ConfiguratorState, index: number) {
  const file = buildDxfSheetDocument(config, index);
  if (!file) return;

  downloadBytes(file.bytes, file.filename, file.contentType);
}

export function openDxfDocument(config: ConfiguratorState) {
  const document = buildDxfDocument(config);

  downloadBytes(document.bytes, document.filename, document.contentType);
}

export async function buildTechnicalDrawingDocument(
  config: ConfiguratorState,
  estimate: ConfiguratorEstimate,
) {
  const [isoImage, bentImage] = await Promise.all([
    renderIsoPreviewImage(config),
    renderBentPreviewImage(config),
  ]);

  return {
    bytes: drawTechnicalPdf(config, estimate, isoImage, bentImage),
    filename: `${getNexExportBaseName(config)}-vyrobny-vykres.pdf`,
    contentType: 'application/pdf',
  };
}

export function buildNexSheetDocument(config: ConfiguratorState, index: number) {
  const file = getNexExportFiles(config)[index];
  if (!file) return null;

  return {
    ...file,
    contentType: 'application/octet-stream',
  };
}

export function buildNexDocument(config: ConfiguratorState) {
  return {
    bytes: createZipFile(getNexExportFiles(config)),
    filename: `${getNexExportBaseName(config)}-vsetky-nex.zip`,
    contentType: 'application/zip',
  };
}

export function buildDxfSheetDocument(config: ConfiguratorState, index: number) {
  const file = getDxfExportFiles(config)[index];
  if (!file) return null;

  return {
    ...file,
    contentType: 'application/dxf',
  };
}

export function buildDxfDocument(config: ConfiguratorState) {
  return {
    bytes: createZipFile(getDxfExportFiles(config)),
    filename: `${getNexExportBaseName(config)}-vsetky-dxf.zip`,
    contentType: 'application/zip',
  };
}
